import React, { useState, useEffect, useRef } from 'react';
import { getServerSessionId } from '../../lib/serverSession';
import { logAdminAction } from '../../lib/adminAuth';
import { downloadInvoicePdf, InvoiceData } from '../../lib/services/invoicePdfService';
import { sendInvoiceNotificationEmail, sendInvoiceCopyEmail } from '../../lib/services/invoiceEmailService';

// Currency configuration
const SUPPORTED_CURRENCIES = [
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'ZAR', symbol: 'R', name: 'South African Rand' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
] as const;

type CurrencyCode = typeof SUPPORTED_CURRENCIES[number]['code'];

interface Invoice {
  id: number;
  invoice_number: string;
  invoice_date: string;
  due_date: string | null;
  user_id: number;
  company_entity: 'SUN' | 'Aureus' | 'UHHF';
  status: 'draft' | 'pending_approval' | 'approved' | 'paid' | 'partial' | 'rejected' | 'voided';
  subtotal: number;
  tax_rate: number;
  tax_amount: number;
  total_amount: number;
  amount_paid: number;
  currency: string;
  payment_requested: boolean;
  payment_requested_at: string | null;
  description: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  user?: {
    id: number;
    username: string;
    email: string;
    full_name: string | null;
  };
  line_items?: InvoiceLineItem[];
  attachments?: InvoiceAttachment[];
}

interface InvoiceLineItem {
  id: number;
  invoice_id: number;
  description: string;
  expense_category: string | null;
  quantity: number;
  unit_price: number;
  amount: number;
  expense_date: string | null;
}

interface InvoiceAttachment {
  id: number;
  invoice_id: number;
  file_name: string;
  file_url: string;
  file_type: string | null;
  attachment_type: 'receipt' | 'proof_of_payment' | 'contract' | 'other';
  description: string | null;
  created_at: string;
}

interface User {
  id: number;
  username: string;
  email: string;
  full_name: string | null;
}

interface InvoiceManagerProps {
  currentUser: any;
}

type ActiveTab = 'all' | 'draft' | 'pending_approval' | 'approved' | 'payment_requested' | 'paid' | 'create' | 'division_revenue' | 'system_log';

interface PhaseBreakdown {
  phaseId: number;
  phaseName: string;
  pricePerShare: number;
  totalRevenue: number;
  purchaseCount: number;
  totalShares: number;
  bonusShares: number;
  aureus: number;
  sun: number;
  uhhf: number;
  affiliates: number;
  goldDiggers: number;
}

interface BonusExpenseEntity {
  totalShares: number;
  totalCost: number;
  purchaseBonusShares?: number;
  purchaseBonusCost?: number;
  consolidatedBonusShares?: number;
  consolidatedBonusCost?: number;
  legacyBonusShares?: number;
  legacyBonusCost?: number;
}

interface BonusExpenses {
  aureus: BonusExpenseEntity;
  sun: BonusExpenseEntity;
}

interface DivisionRevenue {
  grandTotal: number;
  divisionTotals: {
    aureus: number;
    sun: number;
    uhhf: number;
    affiliates: number;
    goldDiggers: number;
  };
  phaseBreakdown: PhaseBreakdown[];
  totalPurchases: number;
  totalSharesSold: number;
  totalBonusShares: number;
  bonusExpenses: BonusExpenses;
}

interface DivisionPayments {
  entityPaid: { Aureus: number; SUN: number; UHHF: number };
  entityInvoices: {
    Aureus: { id: number; invoiceNumber: string; amount: number; paidAt: string }[];
    SUN: { id: number; invoiceNumber: string; amount: number; paidAt: string }[];
    UHHF: { id: number; invoiceNumber: string; amount: number; paidAt: string }[];
  };
}

interface InvoicePayment {
  id: number;
  invoice_id: number;
  payment_date: string;
  payment_amount: number;
  payment_method: string;
  payment_reference: string | null;
  transaction_hash: string | null;
  proof_of_payment_url: string | null;
  admin_notes: string | null;
  created_at: string;
}

interface ProcessPaymentForm {
  payment_method: string;
  payment_reference: string;
  transaction_hash: string;
  proof_of_payment_url: string;
  admin_notes: string;
}

interface InvoiceStats {
  total: number;
  draft: number;
  pending: number;
  approved: number;
  paid: number;
  paymentRequested: number;
  totalAmountPending: number;
  totalAmountPaid: number;
  sunTotal: number;
  aureusTotal: number;
  uhhfTotal: number;
}

export const InvoiceManager: React.FC<InvoiceManagerProps> = ({ currentUser }) => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<ActiveTab>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [entityFilter, setEntityFilter] = useState<'all' | 'SUN' | 'Aureus' | 'UHHF'>('all');
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showProcessPaymentModal, setShowProcessPaymentModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [invoicePayments, setInvoicePayments] = useState<InvoicePayment[]>([]);
  const [processPaymentForm, setProcessPaymentForm] = useState<ProcessPaymentForm>({
    payment_method: 'bank_transfer',
    payment_reference: '',
    transaction_hash: '',
    proof_of_payment_url: '',
    admin_notes: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Auto-dismiss success and error messages after 5 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 8000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Bulk selection state
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState<number[]>([]);
  const [_showBulkActions, _setShowBulkActions] = useState(false);

  // Attachment upload state
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // User payment methods state (for displaying user's banking details)
  const [userPaymentMethods, setUserPaymentMethods] = useState<{
    id: string;
    method_type: 'crypto' | 'bank_transfer';
    method_name: string;
    network_id?: string;
    network_name?: string;
    wallet_address?: string;
    bank_name?: string;
    is_default: boolean;
  }[]>([]);

  // Create/Edit invoice form state
  const [createForm, setCreateForm] = useState({
    user_id: '',
    company_entity: 'SUN' as 'SUN' | 'Aureus' | 'UHHF',
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: '',
    description: '',
    notes: '',
    tax_rate: 0,
    currency: 'USD' as CurrencyCode,
    line_items: [{ id: 0, description: '', expense_category: '', quantity: 1, unit_price: 0, expense_date: '' }]
  });

  // Edit mode tracking
  const [_isEditMode, setIsEditMode] = useState(false);
  const [editingInvoiceId, setEditingInvoiceId] = useState<number | null>(null);

  // Division revenue tracking state
  const [divisionRevenue, setDivisionRevenue] = useState<DivisionRevenue | null>(null);
  const [divisionPayments, setDivisionPayments] = useState<DivisionPayments | null>(null);
  const [divisionLoading, setDivisionLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (activeTab === 'create') {
      setShowCreateModal(true);
    }
    if (activeTab === 'division_revenue') {
      loadDivisionData();
    }
  }, [activeTab]);

  const loadDivisionData = async () => {
    setDivisionLoading(true);
    try {
      const sessionId = getServerSessionId();
      const headers = {
        'Content-Type': 'application/json',
        ...(sessionId ? { 'X-Session-Id': sessionId } : {})
      };
      const [revenueRes, paymentsRes] = await Promise.all([
        fetch('/api/admin/invoices', {
          method: 'POST', headers,
          body: JSON.stringify({ operation: 'load_division_revenue', data: {} })
        }),
        fetch('/api/admin/invoices', {
          method: 'POST', headers,
          body: JSON.stringify({ operation: 'load_division_payments', data: {} })
        })
      ]);
      const revenueResult = await revenueRes.json();
      const paymentsResult = await paymentsRes.json();
      if (revenueResult.success) setDivisionRevenue(revenueResult.data);
      if (paymentsResult.success) setDivisionPayments(paymentsResult.data);
    } catch (err) {
      console.error('Error loading division data:', err);
      setError('Failed to load division revenue data');
    } finally {
      setDivisionLoading(false);
    }
  };

  // Export Division Revenue to CSV
  const exportDivisionRevenueToCSV = () => {
    if (!divisionRevenue) return;
    const headers = ['Phase', 'Price/Share', 'Paid Shares', 'Bonus Shares', 'Revenue', 'Aureus (35%)', 'SUN (25%)', 'UHHF (10%)', 'Affiliates (15%)', 'Gold Diggers (15%)'];
    const rows = divisionRevenue.phaseBreakdown.map(p => [
      p.phaseName, p.pricePerShare.toFixed(2), p.totalShares, p.bonusShares,
      p.totalRevenue.toFixed(2), p.aureus.toFixed(2), p.sun.toFixed(2),
      p.uhhf.toFixed(2), p.affiliates.toFixed(2), p.goldDiggers.toFixed(2)
    ]);
    // Add totals row
    rows.push([
      'TOTALS', '', divisionRevenue.totalSharesSold, divisionRevenue.totalBonusShares,
      divisionRevenue.grandTotal.toFixed(2),
      divisionRevenue.divisionTotals.aureus.toFixed(2),
      divisionRevenue.divisionTotals.sun.toFixed(2),
      divisionRevenue.divisionTotals.uhhf.toFixed(2),
      divisionRevenue.divisionTotals.affiliates.toFixed(2),
      divisionRevenue.divisionTotals.goldDiggers.toFixed(2)
    ]);
    // Add bonus expense summary
    rows.push([]);
    rows.push(['BONUS EXPENSE SUMMARY']);
    rows.push(['Entity', 'Type', 'Shares', 'Cost']);
    if (divisionRevenue.bonusExpenses) {
      const be = divisionRevenue.bonusExpenses;
      if (be.aureus?.purchaseBonusShares) rows.push(['Aureus', 'Purchase Bonuses', be.aureus.purchaseBonusShares, be.aureus.purchaseBonusCost?.toFixed(2) || '']);
      if (be.aureus?.consolidatedBonusShares) rows.push(['Aureus', 'Consolidated Bonuses', be.aureus.consolidatedBonusShares, be.aureus.consolidatedBonusCost?.toFixed(2) || '']);
      rows.push(['Aureus Total', '', be.aureus?.totalShares || 0, be.aureus?.totalCost?.toFixed(2) || '0.00']);
      if (be.sun?.legacyBonusShares) rows.push(['SUN', 'Legacy Bonuses', be.sun.legacyBonusShares, be.sun.legacyBonusCost?.toFixed(2) || '']);
      rows.push(['SUN Total', '', be.sun?.totalShares || 0, be.sun?.totalCost?.toFixed(2) || '0.00']);
      rows.push(['GRAND TOTAL', '', (be.aureus?.totalShares || 0) + (be.sun?.totalShares || 0), ((be.aureus?.totalCost || 0) + (be.sun?.totalCost || 0)).toFixed(2)]);
    }
    const csvEscape = (val: any) => `"${String(val).replace(/"/g, '""')}"`;
    const csvContent = [headers, ...rows].map(r => r.map(csvEscape).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `division_revenue_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Calculate invoice statistics
  const calculateStats = (): InvoiceStats => {
    const stats: InvoiceStats = {
      total: invoices.length,
      draft: 0,
      pending: 0,
      approved: 0,
      paid: 0,
      paymentRequested: 0,
      totalAmountPending: 0,
      totalAmountPaid: 0,
      sunTotal: 0,
      aureusTotal: 0,
      uhhfTotal: 0,
    };

    invoices.forEach(inv => {
      if (inv.status === 'draft') stats.draft++;
      if (inv.status === 'pending_approval') stats.pending++;
      if (inv.status === 'approved') {
        stats.approved++;
        stats.totalAmountPending += inv.total_amount;
        if (inv.payment_requested) stats.paymentRequested++;
      }
      if (inv.status === 'paid') {
        stats.paid++;
        stats.totalAmountPaid += inv.total_amount;
      }
      if (inv.company_entity === 'SUN') stats.sunTotal += inv.total_amount;
      if (inv.company_entity === 'Aureus') stats.aureusTotal += inv.total_amount;
      if (inv.company_entity === 'UHHF') stats.uhhfTotal += inv.total_amount;
    });

    return stats;
  };

  const stats = calculateStats();

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([loadInvoices(), loadUsers()]);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadInvoices = async () => {
    const sessionId = getServerSessionId();
    const response = await fetch('/api/admin/invoices', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(sessionId ? { 'X-Session-Id': sessionId } : {})
      },
      body: JSON.stringify({ operation: 'load_invoices', data: {} })
    });
    const result = await response.json();
    if (!result.success) throw new Error(result.error);
    setInvoices(result.data?.invoices || []);
  };

  const loadUsers = async () => {
    const sessionId = getServerSessionId();
    const response = await fetch('/api/admin/invoices', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(sessionId ? { 'X-Session-Id': sessionId } : {})
      },
      body: JSON.stringify({ operation: 'load_users', data: {} })
    });
    const result = await response.json();
    if (!result.success) throw new Error(result.error);
    setUsers(result.data?.users || []);
  };

  const loadInvoiceDetails = async (invoiceId: number) => {
    const sessionId = getServerSessionId();
    const response = await fetch('/api/admin/invoices', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(sessionId ? { 'X-Session-Id': sessionId } : {})
      },
      body: JSON.stringify({ operation: 'load_invoice_details', data: { invoiceId } })
    });
    const result = await response.json();
    if (!result.success) throw new Error(result.error);

    // Set the data on the selected invoice
    setSelectedInvoice(prev => prev ? {
      ...prev,
      line_items: result.data?.lineItems || [],
      attachments: result.data?.attachments || []
    } : null);
  };

  const handleCreateInvoice = async () => {
    try {
      setLoading(true);
      setError('');

      if (!createForm.user_id) {
        setError('Please select a user');
        return;
      }

      if (createForm.line_items.every(li => !li.description || li.unit_price <= 0)) {
        setError('Please add at least one line item with a description and amount');
        return;
      }

      const sessionId = getServerSessionId();
      const validLineItems = createForm.line_items.filter(li => li.description && li.unit_price > 0);
      const response = await fetch('/api/admin/invoices', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({
          operation: 'create_invoice',
          data: {
            userId: parseInt(createForm.user_id),
            companyEntity: createForm.company_entity,
            invoiceDate: createForm.invoice_date,
            dueDate: createForm.due_date || null,
            description: createForm.description || null,
            notes: createForm.notes || null,
            taxRate: createForm.tax_rate,
            currency: createForm.currency,
            createdByAdminId: currentUser?.adminUser?.id,
            lineItems: validLineItems.map(li => ({
              description: li.description,
              expense_category: li.expense_category || null,
              quantity: li.quantity || 1,
              unit_price: li.unit_price,
              amount: (li.quantity || 1) * li.unit_price,
              expense_date: li.expense_date || null
            }))
          }
        })
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error);

      const newInvoice = result.data?.invoice;

      // Log admin action
      await logAdminAction(
        currentUser?.adminUser?.email || 'unknown',
        'CREATE_INVOICE',
        'invoice',
        newInvoice.id.toString(),
        {},
        { invoice_number: newInvoice.invoice_number, user_id: createForm.user_id, company_entity: createForm.company_entity }
      );

      setSuccess(`Invoice ${newInvoice.invoice_number} created successfully`);
      setShowCreateModal(false);
      setActiveTab('all');
      resetCreateForm();
      await loadInvoices();
    } catch (err) {
      console.error('Error creating invoice:', err);
      setError(err instanceof Error ? err.message : 'Failed to create invoice');
    } finally {
      setLoading(false);
    }
  };

  // Edit existing invoice
  const handleEditInvoice = async () => {
    if (!editingInvoiceId) return;

    try {
      setLoading(true);
      setError('');

      if (createForm.line_items.every(li => !li.description || li.unit_price <= 0)) {
        setError('Please add at least one line item with a description and amount');
        return;
      }

      const sessionId = getServerSessionId();
      const validLineItems = createForm.line_items.filter(li => li.description && li.unit_price > 0);
      const response = await fetch('/api/admin/invoices', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({
          operation: 'update_invoice',
          data: {
            invoiceId: editingInvoiceId,
            invoiceDate: createForm.invoice_date,
            dueDate: createForm.due_date || null,
            description: createForm.description || null,
            notes: createForm.notes || null,
            taxRate: createForm.tax_rate,
            currency: createForm.currency,
            lineItems: validLineItems.map(li => ({
              description: li.description,
              expense_category: li.expense_category || null,
              quantity: li.quantity || 1,
              unit_price: li.unit_price,
              amount: (li.quantity || 1) * li.unit_price,
              expense_date: li.expense_date || null
            }))
          }
        })
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error);

      // Log admin action
      await logAdminAction(
        currentUser?.adminUser?.email || 'unknown',
        'EDIT_INVOICE',
        'invoice',
        editingInvoiceId.toString(),
        {},
        { changes: 'Updated invoice details and line items' }
      );

      setSuccess('Invoice updated successfully');
      setShowEditModal(false);
      setIsEditMode(false);
      setEditingInvoiceId(null);
      resetCreateForm();
      await loadInvoices();
    } catch (err) {
      console.error('Error editing invoice:', err);
      setError(err instanceof Error ? err.message : 'Failed to update invoice');
    } finally {
      setLoading(false);
    }
  };

  // Duplicate invoice
  const handleDuplicateInvoice = async (invoice: Invoice) => {
    try {
      setLoading(true);
      const sessionId = getServerSessionId();
      const response = await fetch('/api/admin/invoices', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({
          operation: 'duplicate_invoice',
          data: {
            originalInvoiceId: invoice.id,
            userId: invoice.user_id,
            companyEntity: invoice.company_entity,
            description: invoice.description ? `Copy of: ${invoice.description}` : 'Duplicated invoice',
            notes: invoice.notes,
            taxRate: invoice.tax_rate,
            currency: invoice.currency,
            dueDate: invoice.due_date,
            invoiceDate: invoice.invoice_date,
            createdByAdminId: currentUser?.adminUser?.id
          }
        })
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error);

      const newInvoice = result.data?.invoice;

      await logAdminAction(
        currentUser?.adminUser?.email || 'unknown',
        'DUPLICATE_INVOICE',
        'invoice',
        newInvoice.id.toString(),
        {},
        { original_invoice_id: invoice.id, original_invoice_number: invoice.invoice_number }
      );

      setSuccess(`Invoice duplicated as ${newInvoice.invoice_number}`);
      await loadInvoices();
    } catch (err) {
      console.error('Error duplicating invoice:', err);
      setError('Failed to duplicate invoice');
    } finally {
      setLoading(false);
    }
  };

  // Open edit modal
  const openEditModal = (invoice: Invoice) => {
    setIsEditMode(true);
    setEditingInvoiceId(invoice.id);
    setCreateForm({
      user_id: invoice.user_id.toString(),
      company_entity: invoice.company_entity,
      invoice_date: invoice.invoice_date,
      due_date: invoice.due_date || '',
      description: invoice.description || '',
      notes: invoice.notes || '',
      tax_rate: invoice.tax_rate,
      currency: (invoice.currency || 'USD') as CurrencyCode,
      line_items: invoice.line_items?.map(li => ({
        id: li.id,
        description: li.description,
        expense_category: li.expense_category || '',
        quantity: li.quantity,
        unit_price: li.unit_price,
        expense_date: li.expense_date || ''
      })) || [{ id: 0, description: '', expense_category: '', quantity: 1, unit_price: 0, expense_date: '' }]
    });
    setShowEditModal(true);
  };

  const handleUpdateStatus = async (invoiceId: number, newStatus: string, reason?: string) => {
    try {
      setLoading(true);
      setError('');

      const invoice = invoices.find(i => i.id === invoiceId);
      const sessionId = getServerSessionId();
      const response = await fetch('/api/admin/invoices', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({
          operation: 'update_status',
          data: {
            invoiceId,
            newStatus,
            reason,
            approvedByAdminId: newStatus === 'approved' ? currentUser?.adminUser?.id : undefined,
            rejectedByAdminId: newStatus === 'rejected' ? currentUser?.adminUser?.id : undefined,
            paidByAdminId: newStatus === 'paid' ? currentUser?.adminUser?.id : undefined
          }
        })
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error);

      // Log admin action
      await logAdminAction(
        currentUser?.adminUser?.email || 'unknown',
        `UPDATE_INVOICE_STATUS_${newStatus.toUpperCase()}`,
        'invoice',
        invoiceId.toString(),
        { status: invoice?.status },
        { status: newStatus, reason }
      );

      // Send email notification for status changes
      if (invoice && ['approved', 'rejected', 'paid'].includes(newStatus)) {
        const emailType = newStatus as 'approved' | 'rejected' | 'paid';
        sendInvoiceNotificationEmail(emailType, {
          invoiceNumber: invoice.invoice_number,
          userName: invoice.user?.full_name || invoice.user?.username || 'User',
          userEmail: invoice.user?.email || '',
          totalAmount: invoice.total_amount,
          currency: invoice.currency || 'USD',
          companyEntity: invoice.company_entity as any,
          status: newStatus,
          rejectionReason: reason
        }).catch(err => console.error('Failed to send invoice notification email:', err));
      }

      setSuccess(`Invoice status updated to ${newStatus}`);
      await loadInvoices();
      setSelectedInvoice(null);
      setShowDetailsModal(false);
    } catch (err) {
      console.error('Error updating invoice status:', err);
      setError(err instanceof Error ? err.message : 'Failed to update invoice');
    } finally {
      setLoading(false);
    }
  };

  // Load payment history for an invoice
  const loadInvoicePayments = async (invoiceId: number) => {
    try {
      const sessionId = getServerSessionId();
      const response = await fetch('/api/admin/invoices', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({ operation: 'load_invoice_payments', data: { invoiceId } })
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error);
      setInvoicePayments(result.data?.payments || []);
    } catch (err) {
      console.error('Error loading invoice payments:', err);
      setInvoicePayments([]);
    }
  };

  // Load user's saved payment methods (for displaying banking details)
  const loadUserPaymentMethods = async (userId: number) => {
    try {
      const sessionId = getServerSessionId();
      const response = await fetch('/api/admin/invoices', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({ operation: 'load_user_payment_methods', data: { userId } })
      });
      const result = await response.json();
      if (!result.success) {
        console.error('Error loading user payment methods:', result.error);
        setUserPaymentMethods([]);
        return;
      }
      setUserPaymentMethods(result.data?.paymentMethods || []);
    } catch (err) {
      console.error('Error loading user payment methods:', err);
      setUserPaymentMethods([]);
    }
  };

  // Process payment for an approved invoice
  const handleProcessPayment = async () => {
    if (!selectedInvoice) return;

    // Confirmation guard — prevent accidental payment processing
    if (!window.confirm(
      `Are you sure you want to process payment of ${formatCurrency(selectedInvoice.total_amount, selectedInvoice.currency)} for invoice ${selectedInvoice.invoice_number}?\n\nThis action will create a financial record and cannot be undone.`
    )) return;

    try {
      setLoading(true);
      setError('');

      // Validate payment method
      if (!processPaymentForm.payment_method) {
        throw new Error('Payment method is required');
      }

      const sessionId = getServerSessionId();
      const response = await fetch('/api/admin/invoices', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({
          operation: 'process_payment',
          data: {
            invoiceId: selectedInvoice.id,
            totalAmount: selectedInvoice.total_amount,
            paymentMethod: processPaymentForm.payment_method,
            paymentReference: processPaymentForm.payment_reference || null,
            transactionHash: processPaymentForm.transaction_hash || null,
            proofOfPaymentUrl: processPaymentForm.proof_of_payment_url || null,
            processedByAdminId: currentUser?.adminUser?.id,
            adminNotes: processPaymentForm.admin_notes || null
          }
        })
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error);

      // Log admin action
      await logAdminAction(
        currentUser?.adminUser?.email || 'unknown',
        'PROCESS_INVOICE_PAYMENT',
        'invoice',
        selectedInvoice.id.toString(),
        { status: 'approved', payment_requested: selectedInvoice.payment_requested },
        {
          status: 'paid',
          payment_id: result.data?.paymentId,
          payment_method: processPaymentForm.payment_method,
          payment_amount: selectedInvoice.total_amount
        }
      );

      // Send email notification
      sendInvoiceNotificationEmail('paid', {
        invoiceNumber: selectedInvoice.invoice_number,
        userName: selectedInvoice.user?.full_name || selectedInvoice.user?.username || 'User',
        userEmail: selectedInvoice.user?.email || '',
        totalAmount: selectedInvoice.total_amount,
        currency: selectedInvoice.currency || 'USD',
        companyEntity: selectedInvoice.company_entity as any,
        status: 'paid',
        paymentReference: processPaymentForm.payment_reference
      }).catch(err => console.error('Failed to send payment notification email:', err));

      setSuccess(`Payment processed successfully for invoice ${selectedInvoice.invoice_number}`);

      // Reset form and close modals
      setProcessPaymentForm({
        payment_method: 'bank_transfer',
        payment_reference: '',
        transaction_hash: '',
        proof_of_payment_url: '',
        admin_notes: ''
      });
      setShowProcessPaymentModal(false);
      setShowDetailsModal(false);
      setSelectedInvoice(null);
      await loadInvoices();
    } catch (err) {
      console.error('Error processing payment:', err);
      setError(err instanceof Error ? err.message : 'Failed to process payment');
    } finally {
      setLoading(false);
    }
  };

  // Open process payment modal
  const openProcessPaymentModal = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setProcessPaymentForm({
      payment_method: 'bank_transfer',
      payment_reference: '',
      transaction_hash: '',
      proof_of_payment_url: '',
      admin_notes: ''
    });
    loadInvoicePayments(invoice.id);
    // Load user's saved payment methods to display their banking details
    if (invoice.user_id) {
      loadUserPaymentMethods(invoice.user_id);
    }
    setShowProcessPaymentModal(true);
  };

  // Email invoice copy to user
  const handleEmailInvoiceToUser = async (invoice: Invoice) => {
    if (!invoice.user?.email) {
      setError('User email not found');
      return;
    }

    setLoading(true);
    try {
      const result = await sendInvoiceCopyEmail({
        invoiceNumber: invoice.invoice_number,
        userName: invoice.user?.full_name || invoice.user?.username || 'User',
        userEmail: invoice.user.email,
        totalAmount: invoice.total_amount,
        currency: invoice.currency || 'USD',
        companyEntity: invoice.company_entity as any,
        status: invoice.status,
        invoiceDate: invoice.invoice_date,
        dueDate: invoice.due_date || undefined
      });

      if (result.success) {
        setSuccess(`Invoice emailed to ${invoice.user.email}`);
        await logAdminAction(
          currentUser?.adminUser?.email || 'unknown',
          'EMAIL_INVOICE_TO_USER',
          'invoice',
          invoice.id.toString(),
          {},
          { recipient: invoice.user.email, invoice_number: invoice.invoice_number }
        );
      } else {
        setError(result.error || 'Failed to send email');
      }
    } catch (err) {
      console.error('Error emailing invoice:', err);
      setError(err instanceof Error ? err.message : 'Failed to email invoice');
    } finally {
      setLoading(false);
    }
  };

  const resetCreateForm = () => {
    setCreateForm({
      user_id: '',
      company_entity: 'SUN',
      invoice_date: new Date().toISOString().split('T')[0],
      due_date: '',
      description: '',
      notes: '',
      tax_rate: 0,
      currency: 'USD',
      line_items: [{ id: 0, description: '', expense_category: '', quantity: 1, unit_price: 0, expense_date: '' }]
    });
    setIsEditMode(false);
    setEditingInvoiceId(null);
  };

  // Handle file upload for attachments
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>, invoiceId: number) => {
    const file = event.target.files?.[0];
    if (!file || !selectedInvoice) return;

    setUploadingAttachment(true);
    try {
      const sessionId = getServerSessionId();

      // Step 1: Request a signed upload URL from the backend
      const signedUrlResponse = await fetch('/api/admin/invoices', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({
          operation: 'upload_attachment',
          data: { invoiceId, fileName: file.name }
        })
      });
      const signedUrlResult = await signedUrlResponse.json();
      if (!signedUrlResult.success) throw new Error(signedUrlResult.error || 'Failed to get upload URL');

      const { signedUrl, path: storagePath } = signedUrlResult.data;

      // Step 2: Upload the binary file directly to Supabase Storage via signed URL
      const uploadResponse = await fetch(signedUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
        body: file
      });
      if (!uploadResponse.ok) throw new Error(`Storage upload failed (${uploadResponse.status})`);

      // Step 3: Save the attachment record with the real storage path
      const recordResponse = await fetch('/api/admin/invoices', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({
          operation: 'save_attachment_record',
          data: {
            invoiceId,
            fileName: file.name,
            fileUrl: storagePath,
            fileType: file.type,
            fileSize: file.size,
            uploadedByAdminId: currentUser?.adminUser?.id
          }
        })
      });
      const recordResult = await recordResponse.json();
      if (!recordResult.success) throw new Error(recordResult.error || 'Failed to save attachment record');

      // Reload attachments
      await loadInvoiceDetails(invoiceId);
      setSuccess('Attachment uploaded successfully');
    } catch (err) {
      console.error('Error uploading attachment:', err);
      setError('Failed to upload attachment');
    } finally {
      setUploadingAttachment(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Delete attachment
  const handleDeleteAttachment = async (attachmentId: number, invoiceId: number) => {
    if (!confirm('Are you sure you want to delete this attachment?')) return;

    try {
      const sessionId = getServerSessionId();
      const response = await fetch('/api/admin/invoices', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({ operation: 'delete_attachment', data: { attachmentId } })
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error);

      await loadInvoiceDetails(invoiceId);
      setSuccess('Attachment deleted');
    } catch (err) {
      console.error('Error deleting attachment:', err);
      setError('Failed to delete attachment');
    }
  };

  // Bulk selection handlers
  const toggleSelectInvoice = (invoiceId: number) => {
    setSelectedInvoiceIds(prev =>
      prev.includes(invoiceId)
        ? prev.filter(id => id !== invoiceId)
        : [...prev, invoiceId]
    );
  };

  const selectAllFilteredInvoices = () => {
    const selectableIds = filteredInvoices
      .filter(inv => ['draft', 'pending_approval'].includes(inv.status))
      .map(inv => inv.id);
    setSelectedInvoiceIds(selectableIds);
  };

  const clearSelection = () => {
    setSelectedInvoiceIds([]);
  };

  // Bulk approve
  const handleBulkApprove = async () => {
    if (selectedInvoiceIds.length === 0) return;
    if (!confirm(`Are you sure you want to approve ${selectedInvoiceIds.length} invoice(s)?`)) return;

    setLoading(true);
    try {
      const sessionId = getServerSessionId();
      const response = await fetch('/api/admin/invoices', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({
          operation: 'bulk_approve',
          data: {
            invoiceIds: selectedInvoiceIds,
            approvedByAdminId: currentUser?.adminUser?.id
          }
        })
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error);

      await logAdminAction(
        currentUser?.adminUser?.email || 'unknown',
        'BULK_APPROVE_INVOICES',
        'invoice',
        selectedInvoiceIds.join(','),
        {},
        { count: selectedInvoiceIds.length }
      );

      setSuccess(`Approved ${selectedInvoiceIds.length} invoice(s)`);
      clearSelection();
      await loadInvoices();
    } catch (err) {
      console.error('Error bulk approving:', err);
      setError('Failed to bulk approve invoices');
    } finally {
      setLoading(false);
    }
  };

  // Bulk reject
  const handleBulkReject = async () => {
    if (selectedInvoiceIds.length === 0) return;
    const reason = prompt('Enter rejection reason for all selected invoices:');
    if (!reason) return;

    setLoading(true);
    try {
      const sessionId = getServerSessionId();
      const response = await fetch('/api/admin/invoices', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({
          operation: 'bulk_reject',
          data: {
            invoiceIds: selectedInvoiceIds,
            rejectedByAdminId: currentUser?.adminUser?.id,
            reason
          }
        })
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error);

      await logAdminAction(
        currentUser?.adminUser?.email || 'unknown',
        'BULK_REJECT_INVOICES',
        'invoice',
        selectedInvoiceIds.join(','),
        {},
        { count: selectedInvoiceIds.length, reason }
      );

      setSuccess(`Rejected ${selectedInvoiceIds.length} invoice(s)`);
      clearSelection();
      await loadInvoices();
    } catch (err) {
      console.error('Error bulk rejecting:', err);
      setError('Failed to bulk reject invoices');
    } finally {
      setLoading(false);
    }
  };

  const addLineItem = () => {
    setCreateForm(prev => ({
      ...prev,
      line_items: [...prev.line_items, { id: 0, description: '', expense_category: '', quantity: 1, unit_price: 0, expense_date: '' }]
    }));
  };

  const removeLineItem = (index: number) => {
    if (createForm.line_items.length > 1) {
      setCreateForm(prev => ({
        ...prev,
        line_items: prev.line_items.filter((_, i) => i !== index)
      }));
    }
  };

  const updateLineItem = (index: number, field: string, value: any) => {
    setCreateForm(prev => ({
      ...prev,
      line_items: prev.line_items.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  // Entity display helpers
  const getEntityLabel = (entity: string) => {
    if (entity === 'SUN') return '☀️ SUN';
    if (entity === 'UHHF') return '🏦 UHHF';
    return '⛏️ Aureus';
  };
  const getEntityFullLabel = (entity: string) => {
    if (entity === 'SUN') return '☀️ SUN (Non-Mining)';
    if (entity === 'UHHF') return '🏦 UHHF (Reserve)';
    return '⛏️ Aureus (Mining)';
  };
  const getEntityBgColor = (entity: string) => {
    if (entity === 'SUN') return '#FEF3C7';
    if (entity === 'UHHF') return '#DBEAFE';
    return '#EDE9FE';
  };
  const getEntityTextColor = (entity: string) => {
    if (entity === 'SUN') return '#92400E';
    if (entity === 'UHHF') return '#1E40AF';
    return '#5B21B6';
  };
  const getEntityAccentColor = (entity: string) => {
    if (entity === 'SUN') return '#FCD34D';
    if (entity === 'UHHF') return '#60A5FA';
    return '#93C5FD';
  };

  const calculateTotal = () => {
    const subtotal = createForm.line_items.reduce((sum, item) =>
      sum + ((item.quantity || 1) * (item.unit_price || 0)), 0
    );
    const taxAmount = subtotal * (createForm.tax_rate / 100);
    return { subtotal, taxAmount, total: subtotal + taxAmount };
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'draft': return { bg: '#374151', text: '#9CA3AF' };
      case 'pending_approval': return { bg: '#78350F', text: '#FCD34D' };
      case 'approved': return { bg: '#065F46', text: '#6EE7B7' };
      case 'paid': return { bg: '#1E40AF', text: '#93C5FD' };
      case 'partial': return { bg: '#7C2D12', text: '#FDBA74' };
      case 'rejected': return { bg: '#7F1D1D', text: '#FCA5A5' };
      case 'voided': return { bg: '#1F2937', text: '#6B7280' };
      default: return { bg: '#374151', text: '#9CA3AF' };
    }
  };

  const formatCurrency = (amount: number, currency?: string) => {
    const currencyCode = currency || 'USD';
    const currencyInfo = SUPPORTED_CURRENCIES.find(c => c.code === currencyCode);
    const symbol = currencyInfo?.symbol || '$';
    return `${symbol}${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  // Export invoices to CSV
  const exportToCSV = () => {
    const headers = ['Invoice Number', 'User', 'Email', 'Company Entity', 'Invoice Date', 'Due Date', 'Status', 'Currency', 'Subtotal', 'Tax Rate', 'Tax Amount', 'Total Amount', 'Payment Requested', 'Description'];
    const rows = filteredInvoices.map(inv => [
      inv.invoice_number,
      inv.user?.full_name || inv.user?.username || '',
      inv.user?.email || '',
      inv.company_entity,
      inv.invoice_date,
      inv.due_date || '',
      inv.status,
      inv.currency || 'USD',
      inv.subtotal.toFixed(2),
      inv.tax_rate.toString(),
      inv.tax_amount.toFixed(2),
      inv.total_amount.toFixed(2),
      inv.payment_requested ? 'Yes' : 'No',
      inv.description || ''
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `invoices_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
    setSuccess(`Exported ${filteredInvoices.length} invoices to CSV`);
  };

  // Tab counts
  const tabCounts = {
    all: invoices.length,
    draft: invoices.filter(i => i.status === 'draft').length,
    pending_approval: invoices.filter(i => i.status === 'pending_approval').length,
    approved: invoices.filter(i => i.status === 'approved').length,
    payment_requested: invoices.filter(i => i.status === 'approved' && i.payment_requested).length,
    paid: invoices.filter(i => i.status === 'paid').length,
    partial: invoices.filter(i => i.status === 'partial').length
  };

  // Filter invoices based on active tab, entity filter, and search term
  const filteredInvoices = invoices.filter(inv => {
    // Payment requested tab - special filter
    if (activeTab === 'payment_requested') {
      if (inv.status !== 'approved' || !inv.payment_requested) return false;
    }
    // Regular status filter
    else if (activeTab !== 'all' && activeTab !== 'create') {
      if (inv.status !== activeTab) return false;
    }

    // Entity filter
    if (entityFilter !== 'all' && inv.company_entity !== entityFilter) return false;

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return (
        inv.invoice_number.toLowerCase().includes(term) ||
        inv.user?.username?.toLowerCase().includes(term) ||
        inv.user?.email?.toLowerCase().includes(term) ||
        inv.user?.full_name?.toLowerCase().includes(term) ||
        inv.description?.toLowerCase().includes(term)
      );
    }
    return true;
  });

  // Pagination
  const ITEMS_PER_PAGE = 20;
  const [currentPage, setCurrentPage] = useState(1);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, entityFilter, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredInvoices.length / ITEMS_PER_PAGE));
  const paginatedInvoices = filteredInvoices.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  // Generate page numbers to display (max 7 visible)
  const getPageNumbers = (): (number | '...')[] => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages: (number | '...')[] = [1];
    if (currentPage > 3) pages.push('...');
    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
      pages.push(i);
    }
    if (currentPage < totalPages - 2) pages.push('...');
    pages.push(totalPages);
    return pages;
  };

  return (
    <div style={{ backgroundColor: '#0F0F0F', minHeight: '100vh', padding: '24px', color: '#FFFFFF' }} className="invoice-manager">
      {/* Responsive CSS */}
      <style>{`
        /* Mobile responsive styles for Invoice Manager */
        @media (max-width: 768px) {
          .invoice-manager { padding: 12px !important; }
          .invoice-manager h1 { font-size: 22px !important; }
          .invoice-manager .stats-grid { grid-template-columns: 1fr 1fr !important; gap: 10px !important; }
          .invoice-manager .tabs-container { overflow-x: auto; -webkit-overflow-scrolling: touch; }
          .invoice-manager .tabs-container button { white-space: nowrap; padding: 8px 12px !important; font-size: 12px !important; }
          .invoice-manager .controls-row { flex-direction: column !important; gap: 8px !important; }
          .invoice-manager .controls-row input,
          .invoice-manager .controls-row select,
          .invoice-manager .controls-row button { width: 100% !important; }
          .invoice-manager .bulk-toolbar { flex-direction: column !important; gap: 10px !important; }
          .invoice-manager .bulk-toolbar > div { width: 100% !important; justify-content: center !important; }
          .invoice-manager .desktop-table { display: none !important; }
          .invoice-manager .mobile-cards { display: flex !important; flex-direction: column !important; gap: 12px !important; }
          .invoice-manager .modal-content { max-width: 100% !important; margin: 10px !important; max-height: 95vh !important; }
          .invoice-manager .modal-grid { grid-template-columns: 1fr !important; }
          .invoice-manager .modal-actions { flex-wrap: wrap !important; gap: 8px !important; }
          .invoice-manager .modal-actions button { flex: 1 1 45% !important; min-width: 120px !important; padding: 8px 10px !important; font-size: 12px !important; }
        }
        @media (max-width: 480px) {
          .invoice-manager { padding: 8px !important; }
          .invoice-manager h1 { font-size: 18px !important; }
          .invoice-manager .stats-grid { grid-template-columns: 1fr !important; }
          .invoice-manager .tabs-container button { padding: 6px 10px !important; font-size: 11px !important; }
          .invoice-manager .modal-actions button { flex: 1 1 100% !important; }
        }
        /* Mobile cards - hidden by default, shown on mobile */
        .invoice-manager .mobile-cards { display: none; }
      `}</style>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#F59E0B', marginBottom: '8px' }}>
            📄 Legal Invoice Management
          </h1>
          <p style={{ color: '#ffffff', fontSize: '16px' }}>
            Create and manage expense reimbursement invoices for SUN, Aureus, and UHHF entities
          </p>
        </div>

        {/* Messages */}
        {error && (
          <div style={{ padding: '12px 16px', backgroundColor: '#7F1D1D', borderRadius: '8px', marginBottom: '16px', color: '#FCA5A5' }}>
            ❌ {error}
          </div>
        )}
        {success && (
          <div style={{ padding: '12px 16px', backgroundColor: '#065F46', borderRadius: '8px', marginBottom: '16px', color: '#6EE7B7' }}>
            ✅ {success}
          </div>
        )}

        {/* Statistics Dashboard */}
        <div className="stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
          <div style={{ backgroundColor: '#1F2937', borderRadius: '12px', padding: '20px', border: '1px solid #374151' }}>
            <div style={{ color: '#ffffff', fontSize: '14px', marginBottom: '4px' }}>Total Invoices</div>
            <div style={{ color: '#ffffff', fontSize: '28px', fontWeight: '700' }}>{stats.total}</div>
            <div style={{ color: '#ffffff', fontSize: '14px', marginTop: '4px' }}>
              {stats.draft} draft • {stats.pending} pending
            </div>
          </div>
          <div style={{ backgroundColor: '#1F2937', borderRadius: '12px', padding: '20px', border: '1px solid #374151' }}>
            <div style={{ color: '#ffffff', fontSize: '14px', marginBottom: '4px' }}>💳 Awaiting Payment</div>
            <div style={{ color: stats.paymentRequested > 0 ? '#EF4444' : '#ffffff', fontSize: '28px', fontWeight: '700' }}>{stats.paymentRequested}</div>
            <div style={{ color: '#ffffff', fontSize: '14px', marginTop: '4px' }}>
              ${stats.totalAmountPending.toLocaleString('en-US', { minimumFractionDigits: 2 })} pending
            </div>
          </div>
          <div style={{ backgroundColor: '#1F2937', borderRadius: '12px', padding: '20px', border: '1px solid #374151' }}>
            <div style={{ color: '#ffffff', fontSize: '14px', marginBottom: '4px' }}>✅ Paid Invoices</div>
            <div style={{ color: '#10B981', fontSize: '28px', fontWeight: '700' }}>{stats.paid}</div>
            <div style={{ color: '#ffffff', fontSize: '14px', marginTop: '4px' }}>
              ${stats.totalAmountPaid.toLocaleString('en-US', { minimumFractionDigits: 2 })} total
            </div>
          </div>
          <div style={{ backgroundColor: '#1F2937', borderRadius: '12px', padding: '20px', border: '1px solid #374151' }}>
            <div style={{ color: '#ffffff', fontSize: '14px', marginBottom: '4px' }}>By Entity</div>
            <div style={{ display: 'flex', gap: '16px' }}>
              <div>
                <div style={{ color: '#F59E0B', fontSize: '16px', fontWeight: '600' }}>☀️ SUN</div>
                <div style={{ color: '#ffffff', fontSize: '14px' }}>${stats.sunTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
              </div>
              <div>
                <div style={{ color: '#8B5CF6', fontSize: '16px', fontWeight: '600' }}>⛏️ Aureus</div>
                <div style={{ color: '#ffffff', fontSize: '14px' }}>${stats.aureusTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
              </div>
              <div>
                <div style={{ color: '#3B82F6', fontSize: '16px', fontWeight: '600' }}>🏦 UHHF</div>
                <div style={{ color: '#ffffff', fontSize: '14px' }}>${stats.uhhfTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="tabs-container" style={{ display: 'flex', gap: '8px', marginBottom: '24px', flexWrap: 'wrap' }}>
          {(['all', 'draft', 'pending_approval', 'approved', 'payment_requested', 'paid'] as const).map(tab => (
            <button
              key={tab}
              onClick={() => { setActiveTab(tab); setShowCreateModal(false); }}
              style={{
                padding: '10px 16px',
                backgroundColor: activeTab === tab ? (tab === 'payment_requested' ? '#DC2626' : '#F59E0B') : '#1F2937',
                color: activeTab === tab ? '#FFFFFF' : '#9CA3AF',
                border: tab === 'payment_requested' && tabCounts.payment_requested > 0 ? '2px solid #DC2626' : 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: activeTab === tab || (tab === 'payment_requested' && tabCounts.payment_requested > 0) ? '600' : '400',
                fontSize: '14px',
                animation: tab === 'payment_requested' && tabCounts.payment_requested > 0 ? 'pulse 2s infinite' : 'none'
              }}
            >
              {tab === 'all' ? '📋 All' :
               tab === 'draft' ? '📝 Draft' :
               tab === 'pending_approval' ? '⏳ Pending' :
               tab === 'approved' ? '✅ Approved' :
               tab === 'payment_requested' ? '💳 Payment Requested' :
               '💰 Paid'}
              {' '}({tabCounts[tab]})
            </button>
          ))}
          <button
            onClick={() => { setActiveTab('division_revenue'); setShowCreateModal(false); }}
            style={{
              padding: '10px 16px',
              backgroundColor: activeTab === 'division_revenue' ? '#3B82F6' : '#1F2937',
              color: activeTab === 'division_revenue' ? '#FFFFFF' : '#9CA3AF',
              border: '1px solid #3B82F6',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: activeTab === 'division_revenue' ? '600' : '400',
              fontSize: '14px'
            }}
          >
            📊 Division Revenue
          </button>
          <button
            onClick={() => { setActiveTab('system_log'); setShowCreateModal(false); }}
            style={{
              padding: '10px 16px',
              backgroundColor: activeTab === 'system_log' ? '#8B5CF6' : '#1F2937',
              color: activeTab === 'system_log' ? '#FFFFFF' : '#9CA3AF',
              border: '1px solid #8B5CF6',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: activeTab === 'system_log' ? '600' : '400',
              fontSize: '14px'
            }}
          >
            🔧 System Log
          </button>
          <button
            onClick={() => { setShowCreateModal(true); resetCreateForm(); }}
            style={{
              padding: '10px 16px',
              backgroundColor: '#10B981',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '14px',
              marginLeft: 'auto'
            }}
          >
            ➕ Create Invoice
          </button>
        </div>

        {/* Division Revenue Dashboard */}
        {activeTab === 'division_revenue' && (
          <div style={{ marginBottom: '24px' }}>
            {divisionLoading ? (
              <div style={{ textAlign: 'center', padding: '60px', color: '#9CA3AF' }}>
                <div style={{ fontSize: '24px', marginBottom: '8px' }}>⏳</div>
                Loading division revenue data...
              </div>
            ) : divisionRevenue ? (
              <>
                {/* Division Summary Cards */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', marginBottom: '24px' }}>
                  {/* Aureus 35% */}
                  <div style={{ backgroundColor: '#1F2937', borderRadius: '12px', padding: '20px', border: '2px solid #8B5CF6' }}>
                    <div style={{ color: '#8B5CF6', fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>⛏️ Aureus (35%)</div>
                    <div style={{ color: '#ffffff', fontSize: '13px', marginBottom: '4px' }}>
                      Revenue Share: <span style={{ fontWeight: '700' }}>${divisionRevenue.divisionTotals.aureus.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div style={{ color: '#FBBF24', fontSize: '12px', marginBottom: '4px' }}>
                      🎁 Bonus Expense: <span style={{ fontWeight: '700' }}>-${(divisionRevenue.bonusExpenses?.aureus?.totalCost || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                      <span style={{ color: '#9CA3AF', fontSize: '10px' }}> ({(divisionRevenue.bonusExpenses?.aureus?.totalShares || 0).toLocaleString()} shares)</span>
                    </div>
                    <div style={{ color: '#ffffff', fontSize: '13px', marginBottom: '4px', borderTop: '1px solid #374151', paddingTop: '4px' }}>
                      Total Owed: <span style={{ color: '#F59E0B', fontWeight: '700' }}>${(divisionRevenue.divisionTotals.aureus - (divisionRevenue.bonusExpenses?.aureus?.totalCost || 0)).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div style={{ color: '#ffffff', fontSize: '13px', marginBottom: '4px' }}>
                      Total Paid: <span style={{ color: '#10B981', fontWeight: '700' }}>${(divisionPayments?.entityPaid.Aureus || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div style={{ color: '#ffffff', fontSize: '13px' }}>
                      Outstanding: <span style={{ color: '#EF4444', fontWeight: '700' }}>${(divisionRevenue.divisionTotals.aureus - (divisionRevenue.bonusExpenses?.aureus?.totalCost || 0) - (divisionPayments?.entityPaid.Aureus || 0)).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                    </div>
                  </div>
                  {/* SUN 25% */}
                  <div style={{ backgroundColor: '#1F2937', borderRadius: '12px', padding: '20px', border: '2px solid #F59E0B' }}>
                    <div style={{ color: '#F59E0B', fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>☀️ SUN (25%)</div>
                    <div style={{ color: '#ffffff', fontSize: '13px', marginBottom: '4px' }}>
                      Revenue Share: <span style={{ fontWeight: '700' }}>${divisionRevenue.divisionTotals.sun.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div style={{ color: '#FBBF24', fontSize: '12px', marginBottom: '4px' }}>
                      🎁 Legacy Bonus Expense: <span style={{ fontWeight: '700' }}>-${(divisionRevenue.bonusExpenses?.sun?.totalCost || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                      <span style={{ color: '#9CA3AF', fontSize: '10px' }}> ({(divisionRevenue.bonusExpenses?.sun?.totalShares || 0).toLocaleString()} shares)</span>
                    </div>
                    <div style={{ color: '#ffffff', fontSize: '13px', marginBottom: '4px', borderTop: '1px solid #374151', paddingTop: '4px' }}>
                      Total Owed: <span style={{ color: '#F59E0B', fontWeight: '700' }}>${(divisionRevenue.divisionTotals.sun - (divisionRevenue.bonusExpenses?.sun?.totalCost || 0)).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div style={{ color: '#ffffff', fontSize: '13px', marginBottom: '4px' }}>
                      Total Paid: <span style={{ color: '#10B981', fontWeight: '700' }}>${(divisionPayments?.entityPaid.SUN || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div style={{ color: '#ffffff', fontSize: '13px' }}>
                      Outstanding: <span style={{ color: '#EF4444', fontWeight: '700' }}>${(divisionRevenue.divisionTotals.sun - (divisionRevenue.bonusExpenses?.sun?.totalCost || 0) - (divisionPayments?.entityPaid.SUN || 0)).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                    </div>
                  </div>
                  {/* UHHF 10% */}
                  <div style={{ backgroundColor: '#1F2937', borderRadius: '12px', padding: '20px', border: '2px solid #10B981' }}>
                    <div style={{ color: '#10B981', fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>🏦 UHHF (10%)</div>
                    <div style={{ color: '#ffffff', fontSize: '13px', marginBottom: '4px' }}>
                      Total Owed: <span style={{ color: '#F59E0B', fontWeight: '700' }}>${divisionRevenue.divisionTotals.uhhf.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div style={{ color: '#ffffff', fontSize: '13px', marginBottom: '4px' }}>
                      Total Paid: <span style={{ color: '#10B981', fontWeight: '700' }}>${(divisionPayments?.entityPaid.UHHF || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div style={{ color: '#ffffff', fontSize: '13px' }}>
                      Outstanding: <span style={{ color: '#EF4444', fontWeight: '700' }}>${(divisionRevenue.divisionTotals.uhhf - (divisionPayments?.entityPaid.UHHF || 0)).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                    </div>
                  </div>
                  {/* Affiliates 15% - read-only */}
                  <div style={{ backgroundColor: '#1F2937', borderRadius: '12px', padding: '20px', border: '1px solid #374151', opacity: 0.75 }}>
                    <div style={{ color: '#60A5FA', fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>🤝 Affiliates (15%)</div>
                    <div style={{ color: '#ffffff', fontSize: '13px', marginBottom: '4px' }}>
                      Allocated: <span style={{ fontWeight: '700' }}>${divisionRevenue.divisionTotals.affiliates.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div style={{ color: '#9CA3AF', fontSize: '11px' }}>Auto-distributed via commissions</div>
                  </div>
                  {/* Gold Diggers 15% - read-only */}
                  <div style={{ backgroundColor: '#1F2937', borderRadius: '12px', padding: '20px', border: '1px solid #374151', opacity: 0.75 }}>
                    <div style={{ color: '#FBBF24', fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>⛏️ Gold Diggers (15%)</div>
                    <div style={{ color: '#ffffff', fontSize: '13px', marginBottom: '4px' }}>
                      Allocated: <span style={{ fontWeight: '700' }}>${divisionRevenue.divisionTotals.goldDiggers.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div style={{ color: '#9CA3AF', fontSize: '11px' }}>Auto-distributed via competition</div>
                  </div>
                </div>

                {/* Grand Total Banner */}
                <div style={{ backgroundColor: '#1E3A5F', borderRadius: '12px', padding: '20px', marginBottom: '24px', border: '1px solid #3B82F6', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
                  <div>
                    <div style={{ color: '#93C5FD', fontSize: '14px' }}>Total Revenue (All Phases)</div>
                    <div style={{ color: '#FFFFFF', fontSize: '28px', fontWeight: '700' }}>${divisionRevenue.grandTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ color: '#93C5FD', fontSize: '14px' }}>Total Paid Shares</div>
                    <div style={{ color: '#FFFFFF', fontSize: '28px', fontWeight: '700' }}>{divisionRevenue.totalSharesSold.toLocaleString()}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ color: '#FBBF24', fontSize: '14px' }}>🎁 All Bonus Shares (Expense)</div>
                    <div style={{ color: '#FBBF24', fontSize: '28px', fontWeight: '700' }}>{((divisionRevenue.bonusExpenses?.aureus?.totalShares || 0) + (divisionRevenue.bonusExpenses?.sun?.totalShares || 0)).toLocaleString()}</div>
                    <div style={{ color: '#9CA3AF', fontSize: '11px' }}>Deducted from division allocations</div>
                  </div>
                </div>

                {/* Per-Phase Breakdown Table */}
                <div style={{ backgroundColor: '#1F2937', borderRadius: '12px', padding: '20px', border: '1px solid #374151' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <h3 style={{ color: '#F59E0B', fontSize: '18px', fontWeight: '600', margin: 0 }}>📊 Revenue Breakdown by Phase</h3>
                    <button
                      onClick={exportDivisionRevenueToCSV}
                      style={{ padding: '8px 16px', backgroundColor: '#10B981', color: '#FFFFFF', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}
                    >
                      📥 Export CSV
                    </button>
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #374151' }}>
                          <th style={{ padding: '12px 8px', textAlign: 'left', color: '#9CA3AF', fontSize: '12px', fontWeight: '600' }}>Phase</th>
                          <th style={{ padding: '12px 8px', textAlign: 'right', color: '#9CA3AF', fontSize: '12px', fontWeight: '600' }}>Paid Shares</th>
                          <th style={{ padding: '12px 8px', textAlign: 'right', color: '#9CA3AF', fontSize: '12px', fontWeight: '600' }}>Revenue</th>
                          <th style={{ padding: '12px 8px', textAlign: 'right', color: '#8B5CF6', fontSize: '12px', fontWeight: '600' }}>Aureus (35%)</th>
                          <th style={{ padding: '12px 8px', textAlign: 'right', color: '#F59E0B', fontSize: '12px', fontWeight: '600' }}>SUN (25%)</th>
                          <th style={{ padding: '12px 8px', textAlign: 'right', color: '#10B981', fontSize: '12px', fontWeight: '600' }}>UHHF (10%)</th>
                          <th style={{ padding: '12px 8px', textAlign: 'right', color: '#60A5FA', fontSize: '12px', fontWeight: '600' }}>Affiliates (15%)</th>
                          <th style={{ padding: '12px 8px', textAlign: 'right', color: '#FBBF24', fontSize: '12px', fontWeight: '600' }}>Gold Diggers (15%)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {divisionRevenue.phaseBreakdown.map(phase => (
                          <tr key={phase.phaseId} style={{ borderBottom: '1px solid #374151' }}>
                            <td style={{ padding: '12px 8px', color: '#FFFFFF', fontSize: '13px', fontWeight: '500' }}>
                              {phase.phaseName}
                              <div style={{ color: '#9CA3AF', fontSize: '11px' }}>${phase.pricePerShare}/share</div>
                            </td>
                            <td style={{ padding: '12px 8px', textAlign: 'right', color: '#FFFFFF', fontSize: '13px' }}>{phase.totalShares.toLocaleString()}</td>
                            <td style={{ padding: '12px 8px', textAlign: 'right', color: '#FFFFFF', fontSize: '13px', fontWeight: '600' }}>${phase.totalRevenue.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                            <td style={{ padding: '12px 8px', textAlign: 'right', color: '#8B5CF6', fontSize: '13px' }}>${phase.aureus.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                            <td style={{ padding: '12px 8px', textAlign: 'right', color: '#F59E0B', fontSize: '13px' }}>${phase.sun.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                            <td style={{ padding: '12px 8px', textAlign: 'right', color: '#10B981', fontSize: '13px' }}>${phase.uhhf.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                            <td style={{ padding: '12px 8px', textAlign: 'right', color: '#60A5FA', fontSize: '13px' }}>${phase.affiliates.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                            <td style={{ padding: '12px 8px', textAlign: 'right', color: '#FBBF24', fontSize: '13px' }}>${phase.goldDiggers.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                          </tr>
                        ))}
                        {/* Totals row */}
                        <tr style={{ borderTop: '2px solid #F59E0B' }}>
                          <td style={{ padding: '12px 8px', color: '#F59E0B', fontSize: '14px', fontWeight: '700' }}>TOTALS</td>
                          <td style={{ padding: '12px 8px', textAlign: 'right', color: '#F59E0B', fontSize: '14px', fontWeight: '700' }}>{divisionRevenue.totalSharesSold.toLocaleString()}</td>
                          <td style={{ padding: '12px 8px', textAlign: 'right', color: '#F59E0B', fontSize: '14px', fontWeight: '700' }}>${divisionRevenue.grandTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                          <td style={{ padding: '12px 8px', textAlign: 'right', color: '#8B5CF6', fontSize: '14px', fontWeight: '700' }}>${divisionRevenue.divisionTotals.aureus.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                          <td style={{ padding: '12px 8px', textAlign: 'right', color: '#F59E0B', fontSize: '14px', fontWeight: '700' }}>${divisionRevenue.divisionTotals.sun.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                          <td style={{ padding: '12px 8px', textAlign: 'right', color: '#10B981', fontSize: '14px', fontWeight: '700' }}>${divisionRevenue.divisionTotals.uhhf.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                          <td style={{ padding: '12px 8px', textAlign: 'right', color: '#60A5FA', fontSize: '14px', fontWeight: '700' }}>${divisionRevenue.divisionTotals.affiliates.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                          <td style={{ padding: '12px 8px', textAlign: 'right', color: '#FBBF24', fontSize: '14px', fontWeight: '700' }}>${divisionRevenue.divisionTotals.goldDiggers.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Bonus Shares Expense Summary */}
                {divisionRevenue.bonusExpenses && (
                  <div style={{ backgroundColor: '#1F2937', borderRadius: '12px', padding: '20px', border: '1px solid #374151', marginTop: '24px' }}>
                    <h3 style={{ color: '#FBBF24', fontSize: '18px', fontWeight: '600', margin: '0 0 8px 0' }}>🎁 Bonus Shares — Expense Summary</h3>
                    <p style={{ color: '#9CA3AF', fontSize: '12px', margin: '0 0 16px 0' }}>
                      Bonus shares are expenses deducted from the responsible division's revenue allocation. They are NOT counted as revenue.
                    </p>
                    <table style={{ width: '100%', borderCollapse: 'collapse', maxWidth: '700px' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #374151' }}>
                          <th style={{ padding: '10px 8px', textAlign: 'left', color: '#9CA3AF', fontSize: '12px', fontWeight: '600' }}>Entity</th>
                          <th style={{ padding: '10px 8px', textAlign: 'left', color: '#9CA3AF', fontSize: '12px', fontWeight: '600' }}>Type</th>
                          <th style={{ padding: '10px 8px', textAlign: 'right', color: '#FBBF24', fontSize: '12px', fontWeight: '600' }}>Shares</th>
                          <th style={{ padding: '10px 8px', textAlign: 'right', color: '#EF4444', fontSize: '12px', fontWeight: '600' }}>Cost</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(divisionRevenue.bonusExpenses.aureus?.purchaseBonusShares || 0) > 0 && (
                          <tr style={{ borderBottom: '1px solid #374151' }}>
                            <td style={{ padding: '10px 8px', color: '#8B5CF6', fontSize: '13px', fontWeight: '600' }}>Aureus</td>
                            <td style={{ padding: '10px 8px', color: '#FFFFFF', fontSize: '13px' }}>Purchase Bonuses</td>
                            <td style={{ padding: '10px 8px', textAlign: 'right', color: '#FBBF24', fontSize: '13px' }}>{(divisionRevenue.bonusExpenses.aureus.purchaseBonusShares || 0).toLocaleString()}</td>
                            <td style={{ padding: '10px 8px', textAlign: 'right', color: '#EF4444', fontSize: '13px' }}>${(divisionRevenue.bonusExpenses.aureus.purchaseBonusCost || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                          </tr>
                        )}
                        {(divisionRevenue.bonusExpenses.aureus?.consolidatedBonusShares || 0) > 0 && (
                          <tr style={{ borderBottom: '1px solid #374151' }}>
                            <td style={{ padding: '10px 8px', color: '#8B5CF6', fontSize: '13px', fontWeight: '600' }}>Aureus</td>
                            <td style={{ padding: '10px 8px', color: '#FFFFFF', fontSize: '13px' }}>Consolidated Bonuses</td>
                            <td style={{ padding: '10px 8px', textAlign: 'right', color: '#FBBF24', fontSize: '13px' }}>{(divisionRevenue.bonusExpenses.aureus.consolidatedBonusShares || 0).toLocaleString()}</td>
                            <td style={{ padding: '10px 8px', textAlign: 'right', color: '#EF4444', fontSize: '13px' }}>${(divisionRevenue.bonusExpenses.aureus.consolidatedBonusCost || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                          </tr>
                        )}
                        <tr style={{ borderBottom: '1px solid #8B5CF6', backgroundColor: 'rgba(139, 92, 246, 0.1)' }}>
                          <td style={{ padding: '10px 8px', color: '#8B5CF6', fontSize: '13px', fontWeight: '700' }}>Aureus Total</td>
                          <td style={{ padding: '10px 8px' }}></td>
                          <td style={{ padding: '10px 8px', textAlign: 'right', color: '#8B5CF6', fontSize: '13px', fontWeight: '700' }}>{(divisionRevenue.bonusExpenses.aureus?.totalShares || 0).toLocaleString()}</td>
                          <td style={{ padding: '10px 8px', textAlign: 'right', color: '#EF4444', fontSize: '13px', fontWeight: '700' }}>${(divisionRevenue.bonusExpenses.aureus?.totalCost || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                        </tr>
                        {(divisionRevenue.bonusExpenses.sun?.legacyBonusShares || 0) > 0 && (
                          <tr style={{ borderBottom: '1px solid #374151' }}>
                            <td style={{ padding: '10px 8px', color: '#F59E0B', fontSize: '13px', fontWeight: '600' }}>SUN</td>
                            <td style={{ padding: '10px 8px', color: '#FFFFFF', fontSize: '13px' }}>Legacy Bonuses</td>
                            <td style={{ padding: '10px 8px', textAlign: 'right', color: '#FBBF24', fontSize: '13px' }}>{(divisionRevenue.bonusExpenses.sun.legacyBonusShares || 0).toLocaleString()}</td>
                            <td style={{ padding: '10px 8px', textAlign: 'right', color: '#EF4444', fontSize: '13px' }}>${(divisionRevenue.bonusExpenses.sun.legacyBonusCost || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                          </tr>
                        )}
                        <tr style={{ borderBottom: '1px solid #F59E0B', backgroundColor: 'rgba(245, 158, 11, 0.1)' }}>
                          <td style={{ padding: '10px 8px', color: '#F59E0B', fontSize: '13px', fontWeight: '700' }}>SUN Total</td>
                          <td style={{ padding: '10px 8px' }}></td>
                          <td style={{ padding: '10px 8px', textAlign: 'right', color: '#F59E0B', fontSize: '13px', fontWeight: '700' }}>{(divisionRevenue.bonusExpenses.sun?.totalShares || 0).toLocaleString()}</td>
                          <td style={{ padding: '10px 8px', textAlign: 'right', color: '#EF4444', fontSize: '13px', fontWeight: '700' }}>${(divisionRevenue.bonusExpenses.sun?.totalCost || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                        </tr>
                        <tr style={{ borderTop: '2px solid #FBBF24' }}>
                          <td style={{ padding: '10px 8px', color: '#FBBF24', fontSize: '14px', fontWeight: '700' }}>GRAND TOTAL</td>
                          <td style={{ padding: '10px 8px' }}></td>
                          <td style={{ padding: '10px 8px', textAlign: 'right', color: '#FBBF24', fontSize: '14px', fontWeight: '700' }}>{((divisionRevenue.bonusExpenses.aureus?.totalShares || 0) + (divisionRevenue.bonusExpenses.sun?.totalShares || 0)).toLocaleString()}</td>
                          <td style={{ padding: '10px 8px', textAlign: 'right', color: '#EF4444', fontSize: '14px', fontWeight: '700' }}>${((divisionRevenue.bonusExpenses.aureus?.totalCost || 0) + (divisionRevenue.bonusExpenses.sun?.totalCost || 0)).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: '60px', color: '#9CA3AF' }}>
                Failed to load division revenue data. Please try again.
              </div>
            )}
          </div>
        )}

        {/* System Log */}
        {activeTab === 'system_log' && (
          <div style={{ marginBottom: '24px' }}>
            <div style={{ backgroundColor: '#1F2937', borderRadius: '12px', padding: '24px', border: '1px solid #374151' }}>
              <h3 style={{ color: '#8B5CF6', fontWeight: '700', fontSize: '18px', marginBottom: '8px' }}>🔧 Invoice System Changelog</h3>
              <p style={{ color: '#9CA3AF', fontSize: '14px', marginBottom: '20px' }}>All upgrades, audits, and fixes applied to the invoice system.</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {[
                  { ver: '2.9.46', date: '2026-03-15', tag: 'PERF', tagColor: '#10B981', title: 'Pagination', desc: 'Client-side pagination (20/page) for desktop table and mobile cards. Prevents UI breaking with 119+ records.' },
                  { ver: '2.9.43', date: '2026-03-15', tag: 'DATA', tagColor: '#3B82F6', title: 'Manual Commission Invoicing', desc: 'Created 119 invoices (SUN-COMM-001→119) for all manually-added commission records. All assigned to ☀️ SUN, status=paid. Total: $148,517.56. Full audit trail via notes field.' },
                  { ver: '2.9.42', date: '2026-03-15', tag: 'AUDIT', tagColor: '#F59E0B', title: 'Invoice System Audit v6', desc: '6 fixes: (1) Backend persists created_by_admin_id. (2) process_payment sets paid_by_admin_id + clears payment_requested. (3) TypeScript interface updated. (4) Partial payment badge. (5) Auto-dismiss banners. (6) Payment confirmation guard.' },
                  { ver: '2.9.41', date: '2026-03-15', tag: 'AUDIT', tagColor: '#F59E0B', title: 'Invoice System Audit v5', desc: '4 fixes: (1) Attachment upload pipeline fixed — signed URL for binary PUT. (2) Division Revenue CSV export uses RFC 4180 escaping. (3) Backend persists paid_by_admin_id on status change. (4) Duplicate invoice carries over all date fields.' },
                  { ver: '2.9.40', date: '2026-03-15', tag: 'AUDIT', tagColor: '#F59E0B', title: 'Invoice System Audit v4', desc: '6 fixes: (1) Bulk approve/reject refactored to batch update. (2) Quick Pay sets paid_at timestamp. (3) Edit modal line items include Category/Expense Date. (4) Notes textarea in Create/Edit. (5) process_payment returns actual payment ID. (6) Division Revenue CSV export button.' },
                  { ver: '2.9.39', date: '2026-03-15', tag: 'AUDIT', tagColor: '#F59E0B', title: 'Invoice System Deep Audit', desc: '5 fixes: (1) Backend update_invoice persists invoice_date, description, tax_rate. (2) process_payment aligned with frontend fields. (3) Tax calculations fixed across create/update/duplicate. (4) Line items include expense_category, expense_date, amount. (5) duplicate_invoice fixed.' },
                  { ver: '2.9.38', date: '2026-03-14', tag: 'FEATURE', tagColor: '#8B5CF6', title: 'Master Audit KYC Upgrade', desc: 'Full KYC info in audit records (legal name, email, phone, ID type, country). Vulnerability detection. Country/KYC/Vulnerability filters. Flagged rows highlighted red.' },
                  { ver: '2.9.37', date: '2026-03-14', tag: 'FEATURE', tagColor: '#8B5CF6', title: 'Invoice System Foundation', desc: 'Full UHHF entity support. Backend create/duplicate now persist company_entity, invoice_date, description, tax_rate. Frontend supports 3 entities (SUN/Aureus/UHHF). Centralized entity helpers. Mobile card View button crash fixed.' },
                  { ver: '2.9.36', date: '2026-03-14', tag: 'FEATURE', tagColor: '#8B5CF6', title: 'Division Revenue Dashboard', desc: 'Per-division revenue tracking (Aureus 35%, SUN 25%, UHHF 10%). Per-phase breakdown table. Total Owed vs Total Paid with Outstanding balance. Affiliates (15%) and Gold Diggers (15%) shown as reference.' },
                ].map((entry, idx) => (
                  <div key={idx} style={{ backgroundColor: '#111827', borderRadius: '10px', padding: '16px', border: '1px solid #374151' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px', flexWrap: 'wrap' }}>
                      <span style={{ padding: '3px 10px', borderRadius: '999px', backgroundColor: entry.tagColor + '22', color: entry.tagColor, fontSize: '12px', fontWeight: '700' }}>{entry.tag}</span>
                      <span style={{ color: '#FFFFFF', fontWeight: '700', fontSize: '15px' }}>{entry.title}</span>
                      <span style={{ color: '#6B7280', fontSize: '13px', marginLeft: 'auto' }}>v{entry.ver} · {entry.date}</span>
                    </div>
                    <p style={{ color: '#D1D5DB', fontSize: '14px', margin: 0, lineHeight: '1.5' }}>{entry.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Invoice list section - hidden on division revenue and system log tabs */}
        {activeTab !== 'division_revenue' && activeTab !== 'system_log' && (<>
        {/* Filters */}
        <div className="controls-row" style={{ display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' }}>
          <input
            type="text"
            placeholder="Search by invoice #, user, or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              flex: 1,
              minWidth: '200px',
              padding: '10px 14px',
              backgroundColor: '#1F2937',
              border: '1px solid #374151',
              borderRadius: '8px',
              color: '#FFFFFF',
              fontSize: '14px'
            }}
          />
          <select
            value={entityFilter}
            onChange={(e) => setEntityFilter(e.target.value as any)}
            style={{
              padding: '10px 14px',
              backgroundColor: '#1F2937',
              border: '1px solid #374151',
              borderRadius: '8px',
              color: '#FFFFFF',
              fontSize: '14px',
              cursor: 'pointer'
            }}
          >
            <option value="all">All Entities</option>
            <option value="SUN">☀️ SUN (Non-Mining)</option>
            <option value="Aureus">⛏️ Aureus (Mining)</option>
            <option value="UHHF">🏦 UHHF (Reserve)</option>
          </select>
          <button
            onClick={loadData}
            disabled={loading}
            style={{
              padding: '10px 16px',
              backgroundColor: '#374151',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            🔄 Refresh
          </button>
          <button
            onClick={exportToCSV}
            disabled={filteredInvoices.length === 0}
            style={{
              padding: '10px 16px',
              backgroundColor: '#065F46',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '8px',
              cursor: filteredInvoices.length === 0 ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              opacity: filteredInvoices.length === 0 ? 0.5 : 1
            }}
          >
            📥 Export CSV
          </button>
        </div>

        {/* Loading State */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '48px' }}>
            <div style={{ fontSize: '24px', marginBottom: '16px' }}>⏳</div>
            <p style={{ color: '#ffffff' }}>Loading invoices...</p>
          </div>
        )}

        {/* Bulk Actions Toolbar */}
        {selectedInvoiceIds.length > 0 && (
          <div className="bulk-toolbar" style={{
            backgroundColor: '#1E3A5F',
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            border: '1px solid #3B82F6',
            flexWrap: 'wrap',
            gap: '12px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ color: '#93C5FD', fontWeight: '600' }}>
                ✓ {selectedInvoiceIds.length} invoice(s) selected
              </span>
              <button
                onClick={clearSelection}
                style={{
                  padding: '6px 12px',
                  backgroundColor: 'transparent',
                  color: '#ffffff',
                  border: '1px solid #4B5563',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px'
                }}
              >
                Clear Selection
              </button>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={handleBulkApprove}
                disabled={loading}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#065F46',
                  color: '#6EE7B7',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  fontSize: '13px',
                  opacity: loading ? 0.5 : 1
                }}
              >
                ✓ Approve All
              </button>
              <button
                onClick={handleBulkReject}
                disabled={loading}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#7F1D1D',
                  color: '#FCA5A5',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  fontSize: '13px',
                  opacity: loading ? 0.5 : 1
                }}
              >
                ✗ Reject All
              </button>
            </div>
          </div>
        )}

        {/* Invoices Table - Desktop */}
        {!loading && (
          <div className="desktop-table" style={{ backgroundColor: '#1F2937', borderRadius: '12px', border: '1px solid #374151', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#111827' }}>
                  <th style={{ padding: '14px 10px', textAlign: 'center', width: '40px' }}>
                    <input
                      type="checkbox"
                      checked={selectedInvoiceIds.length > 0 && selectedInvoiceIds.length === filteredInvoices.filter(inv => ['draft', 'pending_approval'].includes(inv.status)).length}
                      onChange={(e) => e.target.checked ? selectAllFilteredInvoices() : clearSelection()}
                      style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                      title="Select all"
                    />
                  </th>
                  <th style={{ padding: '14px 16px', textAlign: 'left', color: '#ffffff', fontWeight: '600', fontSize: '13px' }}>Invoice #</th>
                  <th style={{ padding: '14px 16px', textAlign: 'left', color: '#ffffff', fontWeight: '600', fontSize: '13px' }}>User</th>
                  <th style={{ padding: '14px 16px', textAlign: 'left', color: '#ffffff', fontWeight: '600', fontSize: '13px' }}>Entity</th>
                  <th style={{ padding: '14px 16px', textAlign: 'left', color: '#ffffff', fontWeight: '600', fontSize: '13px' }}>Date</th>
                  <th style={{ padding: '14px 16px', textAlign: 'right', color: '#ffffff', fontWeight: '600', fontSize: '13px' }}>Amount</th>
                  <th style={{ padding: '14px 16px', textAlign: 'center', color: '#ffffff', fontWeight: '600', fontSize: '13px' }}>Status</th>
                  <th style={{ padding: '14px 16px', textAlign: 'center', color: '#ffffff', fontWeight: '600', fontSize: '13px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredInvoices.length === 0 ? (
                  <tr>
                    <td colSpan={8} style={{ padding: '32px', textAlign: 'center', color: '#ffffff' }}>
                      No invoices found
                    </td>
                  </tr>
                ) : (
                  paginatedInvoices.map(invoice => {
                    const statusColors = getStatusBadgeColor(invoice.status);
                    const isSelectable = ['draft', 'pending_approval'].includes(invoice.status);
                    const isSelected = selectedInvoiceIds.includes(invoice.id);
                    return (
                      <tr key={invoice.id} style={{ borderTop: '1px solid #374151', backgroundColor: isSelected ? '#1E3A5F' : 'transparent' }}>
                        <td style={{ padding: '14px 10px', textAlign: 'center' }}>
                          {isSelectable ? (
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleSelectInvoice(invoice.id)}
                              style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                            />
                          ) : (
                            <span style={{ color: '#4B5563' }}>-</span>
                          )}
                        </td>
                        <td style={{ padding: '14px 16px', color: '#ffffff', fontWeight: '500' }}>{invoice.invoice_number}</td>
                        <td style={{ padding: '14px 16px' }}>
                          <div style={{ color: '#ffffff' }}>{invoice.user?.full_name || invoice.user?.username}</div>
                          <div style={{ color: '#ffffff', fontSize: '14px' }}>{invoice.user?.email}</div>
                        </td>
                        <td style={{ padding: '14px 16px' }}>
                          <span style={{
                            padding: '4px 8px',
                            borderRadius: '4px',
                            backgroundColor: getEntityBgColor(invoice.company_entity),
                            color: getEntityTextColor(invoice.company_entity),
                            fontSize: '14px',
                            fontWeight: '500'
                          }}>
                            {getEntityLabel(invoice.company_entity)}
                          </span>
                        </td>
                        <td style={{ padding: '14px 16px', color: '#ffffff' }}>{formatDate(invoice.invoice_date)}</td>
                        <td style={{ padding: '14px 16px', textAlign: 'right', color: '#10B981', fontWeight: '600' }}>{formatCurrency(invoice.total_amount)}</td>
                        <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                            <span style={{
                              padding: '4px 10px',
                              borderRadius: '999px',
                              backgroundColor: statusColors.bg,
                              color: statusColors.text,
                              fontSize: '14px',
                              fontWeight: '500'
                            }}>
                              {invoice.status.replace('_', ' ')}
                            </span>
                            {invoice.status === 'approved' && invoice.payment_requested && (
                              <span style={{
                                padding: '2px 8px',
                                borderRadius: '999px',
                                backgroundColor: '#7F1D1D',
                                color: '#FCA5A5',
                                fontSize: '13px',
                                fontWeight: '600'
                              }}>
                                💳 PAYMENT REQUESTED
                              </span>
                            )}
                          </div>
                        </td>
                        <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                            <button
                              onClick={() => {
                                setSelectedInvoice(invoice);
                                loadInvoiceDetails(invoice.id);
                                setShowDetailsModal(true);
                              }}
                              style={{
                                padding: '6px 12px',
                                backgroundColor: '#374151',
                                color: '#ffffff',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '13px'
                              }}
                            >
                              👁️ View
                            </button>
                            {invoice.status === 'approved' && invoice.payment_requested && (
                              <button
                                onClick={() => openProcessPaymentModal(invoice)}
                                style={{
                                  padding: '6px 12px',
                                  backgroundColor: '#DC2626',
                                  color: '#FFFFFF',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '13px',
                                  fontWeight: '600'
                                }}
                              >
                                💳 Pay
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Invoices Cards - Mobile */}
        {!loading && (
          <div className="mobile-cards" style={{ flexDirection: 'column', gap: '12px' }}>
            {filteredInvoices.length === 0 ? (
              <div style={{ padding: '32px', textAlign: 'center', color: '#ffffff', backgroundColor: '#1F2937', borderRadius: '12px' }}>
                No invoices found
              </div>
            ) : (
              paginatedInvoices.map(invoice => {
                const statusColors = getStatusBadgeColor(invoice.status);
                const isSelectable = ['draft', 'pending_approval'].includes(invoice.status);
                const isSelected = selectedInvoiceIds.includes(invoice.id);
                return (
                  <div
                    key={invoice.id}
                    style={{
                      backgroundColor: isSelected ? '#1E3A5F' : '#1F2937',
                      borderRadius: '12px',
                      padding: '16px',
                      border: `1px solid ${isSelected ? '#3B82F6' : '#374151'}`
                    }}
                  >
                    {/* Card Header */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {isSelectable && (
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSelectInvoice(invoice.id)}
                            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                          />
                        )}
                        <div>
                          <div style={{ color: '#ffffff', fontWeight: '600', fontSize: '15px' }}>{invoice.invoice_number}</div>
                          <div style={{ color: '#ffffff', fontSize: '14px' }}>{formatDate(invoice.invoice_date)}</div>
                        </div>
                      </div>
                      <span style={{
                        padding: '4px 10px',
                        borderRadius: '999px',
                        backgroundColor: statusColors.bg,
                        color: statusColors.text,
                        fontSize: '13px',
                        fontWeight: '500'
                      }}>
                        {invoice.status.replace('_', ' ')}
                      </span>
                    </div>

                    {/* Card Body */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '12px' }}>
                      <div>
                        <div style={{ color: '#ffffff', fontSize: '13px' }}>User</div>
                        <div style={{ color: '#ffffff', fontSize: '13px' }}>{invoice.user?.full_name || invoice.user?.username}</div>
                      </div>
                      <div>
                        <div style={{ color: '#ffffff', fontSize: '13px' }}>Entity</div>
                        <span style={{
                          padding: '2px 6px',
                          borderRadius: '4px',
                          backgroundColor: getEntityBgColor(invoice.company_entity),
                          color: getEntityTextColor(invoice.company_entity),
                          fontSize: '13px',
                          fontWeight: '500'
                        }}>
                          {getEntityLabel(invoice.company_entity)}
                        </span>
                      </div>
                    </div>

                    {/* Amount */}
                    <div style={{ backgroundColor: '#111827', borderRadius: '8px', padding: '10px', marginBottom: '12px', textAlign: 'center' }}>
                      <div style={{ color: '#10B981', fontWeight: '700', fontSize: '20px' }}>{formatCurrency(invoice.total_amount, invoice.currency)}</div>
                      {invoice.status === 'approved' && invoice.payment_requested && (
                        <span style={{
                          display: 'inline-block',
                          marginTop: '6px',
                          padding: '2px 8px',
                          borderRadius: '999px',
                          backgroundColor: '#7F1D1D',
                          color: '#FCA5A5',
                          fontSize: '13px',
                          fontWeight: '600'
                        }}>
                          💳 PAYMENT REQUESTED
                        </span>
                      )}
                    </div>

                    {/* Card Actions */}
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <button
                        onClick={() => { setSelectedInvoice(invoice); loadInvoiceDetails(invoice.id); setShowDetailsModal(true); }}
                        style={{
                          flex: 1,
                          padding: '8px 12px',
                          backgroundColor: '#374151',
                          color: '#FFFFFF',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: '500'
                        }}
                      >
                        👁️ View
                      </button>
                      {invoice.status === 'approved' && invoice.payment_requested && (
                        <button
                          onClick={() => openProcessPaymentModal(invoice)}
                          style={{
                            flex: 1,
                            padding: '8px 12px',
                            backgroundColor: '#F59E0B',
                            color: '#000000',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '600'
                          }}
                        >
                          💳 Pay
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* Pagination Controls */}
        {filteredInvoices.length > ITEMS_PER_PAGE && (
          <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              style={{
                padding: '8px 14px', backgroundColor: currentPage === 1 ? '#1F2937' : '#374151',
                color: currentPage === 1 ? '#6B7280' : '#FFFFFF', border: '1px solid #4B5563',
                borderRadius: '8px', cursor: currentPage === 1 ? 'default' : 'pointer', fontSize: '14px'
              }}
            >
              ← Prev
            </button>
            {getPageNumbers().map((page, idx) =>
              page === '...' ? (
                <span key={`dots-${idx}`} style={{ color: '#6B7280', padding: '0 4px' }}>…</span>
              ) : (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  style={{
                    padding: '8px 12px', backgroundColor: currentPage === page ? '#F59E0B' : '#374151',
                    color: currentPage === page ? '#000' : '#FFF', border: '1px solid #4B5563',
                    borderRadius: '8px', cursor: 'pointer', fontWeight: currentPage === page ? '700' : '400', fontSize: '14px'
                  }}
                >
                  {page}
                </button>
              )
            )}
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              style={{
                padding: '8px 14px', backgroundColor: currentPage === totalPages ? '#1F2937' : '#374151',
                color: currentPage === totalPages ? '#6B7280' : '#FFFFFF', border: '1px solid #4B5563',
                borderRadius: '8px', cursor: currentPage === totalPages ? 'default' : 'pointer', fontSize: '14px'
              }}
            >
              Next →
            </button>
          </div>
        )}
        {/* Summary */}
        <div style={{ marginTop: '12px', textAlign: 'center', color: '#9CA3AF', fontSize: '14px' }}>
          Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1}–{Math.min(currentPage * ITEMS_PER_PAGE, filteredInvoices.length)} of {filteredInvoices.length} invoices
          {filteredInvoices.length < invoices.length && ` (filtered from ${invoices.length} total)`}
        </div>
        </>)}

        {/* Create Invoice Modal */}
        {showCreateModal && (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '12px' }}>
            <div className="modal-content" style={{ backgroundColor: '#1F2937', borderRadius: '16px', width: '100%', maxWidth: '800px', maxHeight: '90vh', overflow: 'auto', border: '1px solid #374151' }}>
              <div style={{ padding: '20px', borderBottom: '1px solid #374151', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#F59E0B' }}>➕ Create New Invoice</h2>
                <button onClick={() => { setShowCreateModal(false); setActiveTab('all'); }} style={{ background: 'none', border: 'none', color: '#ffffff', fontSize: '24px', cursor: 'pointer' }}>×</button>
              </div>
              <div style={{ padding: '20px' }}>
                <div className="modal-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '20px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', color: '#ffffff', fontSize: '14px' }}>User *</label>
                    <select
                      value={createForm.user_id}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, user_id: e.target.value }))}
                      style={{ width: '100%', padding: '10px', backgroundColor: '#111827', border: '1px solid #374151', borderRadius: '8px', color: '#FFFFFF' }}
                    >
                      <option value="">Select a user...</option>
                      {users.map(user => (
                        <option key={user.id} value={user.id}>
                          {user.full_name || user.username} ({user.email})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', color: '#ffffff', fontSize: '14px' }}>Company Entity *</label>
                    <select
                      value={createForm.company_entity}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, company_entity: e.target.value as any }))}
                      style={{ width: '100%', padding: '10px', backgroundColor: '#111827', border: '1px solid #374151', borderRadius: '8px', color: '#FFFFFF' }}
                    >
                      <option value="SUN">☀️ SUN (Non-Mining)</option>
                      <option value="Aureus">⛏️ Aureus (Mining)</option>
                      <option value="UHHF">🏦 UHHF (Reserve)</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', color: '#ffffff', fontSize: '14px' }}>Currency *</label>
                    <select
                      value={createForm.currency}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, currency: e.target.value as CurrencyCode }))}
                      style={{ width: '100%', padding: '10px', backgroundColor: '#111827', border: '1px solid #374151', borderRadius: '8px', color: '#FFFFFF' }}
                    >
                      {SUPPORTED_CURRENCIES.map(curr => (
                        <option key={curr.code} value={curr.code}>
                          {curr.symbol} {curr.code} - {curr.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', color: '#ffffff', fontSize: '14px' }}>Invoice Date</label>
                    <input
                      type="date"
                      value={createForm.invoice_date}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, invoice_date: e.target.value }))}
                      style={{ width: '100%', padding: '10px', backgroundColor: '#111827', border: '1px solid #374151', borderRadius: '8px', color: '#FFFFFF' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', color: '#ffffff', fontSize: '14px' }}>Due Date</label>
                    <input
                      type="date"
                      value={createForm.due_date}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, due_date: e.target.value }))}
                      style={{ width: '100%', padding: '10px', backgroundColor: '#111827', border: '1px solid #374151', borderRadius: '8px', color: '#FFFFFF' }}
                    />
                  </div>
                </div>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', color: '#ffffff', fontSize: '14px' }}>Description</label>
                  <textarea
                    value={createForm.description}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Brief description of the invoice..."
                    rows={2}
                    style={{ width: '100%', padding: '10px', backgroundColor: '#111827', border: '1px solid #374151', borderRadius: '8px', color: '#FFFFFF', resize: 'vertical' }}
                  />
                </div>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', color: '#ffffff', fontSize: '14px' }}>Notes (Internal)</label>
                  <textarea
                    value={createForm.notes}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Internal notes (not shown on invoice)..."
                    rows={2}
                    style={{ width: '100%', padding: '10px', backgroundColor: '#111827', border: '1px solid #374151', borderRadius: '8px', color: '#FFFFFF', resize: 'vertical' }}
                  />
                </div>

                {/* Line Items */}
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <label style={{ color: '#ffffff', fontSize: '16px', fontWeight: '600' }}>Line Items</label>
                    <button onClick={addLineItem} style={{ padding: '6px 12px', backgroundColor: '#10B981', color: '#FFFFFF', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>
                      + Add Item
                    </button>
                  </div>
                  {createForm.line_items.map((item, index) => (
                    <div key={index} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr auto', gap: '8px', marginBottom: '8px', alignItems: 'end' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: '4px', color: '#ffffff', fontSize: '14px' }}>Description</label>
                        <input
                          type="text"
                          value={item.description}
                          onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                          placeholder="Expense description..."
                          style={{ width: '100%', padding: '8px', backgroundColor: '#111827', border: '1px solid #374151', borderRadius: '6px', color: '#FFFFFF', fontSize: '13px' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '4px', color: '#ffffff', fontSize: '14px' }}>Category</label>
                        <input
                          type="text"
                          value={item.expense_category}
                          onChange={(e) => updateLineItem(index, 'expense_category', e.target.value)}
                          placeholder="e.g. Travel"
                          style={{ width: '100%', padding: '8px', backgroundColor: '#111827', border: '1px solid #374151', borderRadius: '6px', color: '#FFFFFF', fontSize: '13px' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '4px', color: '#ffffff', fontSize: '14px' }}>Qty</label>
                        <input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) => updateLineItem(index, 'quantity', parseFloat(e.target.value) || 1)}
                          style={{ width: '100%', padding: '8px', backgroundColor: '#111827', border: '1px solid #374151', borderRadius: '6px', color: '#FFFFFF', fontSize: '13px' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '4px', color: '#ffffff', fontSize: '14px' }}>Unit Price ($)</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.unit_price}
                          onChange={(e) => updateLineItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                          style={{ width: '100%', padding: '8px', backgroundColor: '#111827', border: '1px solid #374151', borderRadius: '6px', color: '#FFFFFF', fontSize: '13px' }}
                        />
                      </div>
                      <button
                        onClick={() => removeLineItem(index)}
                        disabled={createForm.line_items.length === 1}
                        style={{ padding: '8px 10px', backgroundColor: '#7F1D1D', color: '#FCA5A5', border: 'none', borderRadius: '6px', cursor: createForm.line_items.length === 1 ? 'not-allowed' : 'pointer', opacity: createForm.line_items.length === 1 ? 0.5 : 1 }}
                      >
                        🗑️
                      </button>
                    </div>
                  ))}
                </div>

                {/* Totals */}
                <div style={{ backgroundColor: '#111827', borderRadius: '8px', padding: '16px', marginBottom: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={{ color: '#ffffff' }}>Subtotal:</span>
                    <span style={{ color: '#ffffff' }}>{formatCurrency(calculateTotal().subtotal)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ color: '#ffffff' }}>Tax:</span>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        value={createForm.tax_rate}
                        onChange={(e) => setCreateForm(prev => ({ ...prev, tax_rate: parseFloat(e.target.value) || 0 }))}
                        style={{ width: '60px', padding: '4px 8px', backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '4px', color: '#FFFFFF', fontSize: '13px', textAlign: 'right' }}
                      />
                      <span style={{ color: '#ffffff' }}>%</span>
                    </div>
                    <span style={{ color: '#ffffff' }}>{formatCurrency(calculateTotal().taxAmount)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #374151', paddingTop: '8px' }}>
                    <span style={{ color: '#ffffff', fontWeight: '600' }}>Total:</span>
                    <span style={{ color: '#10B981', fontWeight: '700', fontSize: '18px' }}>{formatCurrency(calculateTotal().total)}</span>
                  </div>
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => { setShowCreateModal(false); setActiveTab('all'); }}
                    style={{ padding: '10px 20px', backgroundColor: '#374151', color: '#ffffff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateInvoice}
                    disabled={loading}
                    style={{ padding: '10px 20px', backgroundColor: '#10B981', color: '#FFFFFF', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', opacity: loading ? 0.6 : 1 }}
                  >
                    {loading ? 'Creating...' : '✅ Create Invoice'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Invoice Modal */}
        {showEditModal && (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '24px' }}>
            <div style={{ backgroundColor: '#1F2937', borderRadius: '16px', width: '100%', maxWidth: '800px', maxHeight: '90vh', overflow: 'auto', border: '1px solid #374151' }}>
              <div style={{ padding: '24px', borderBottom: '1px solid #374151', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#3B82F6' }}>✏️ Edit Invoice</h2>
                <button onClick={() => { setShowEditModal(false); resetCreateForm(); }} style={{ background: 'none', border: 'none', color: '#ffffff', fontSize: '24px', cursor: 'pointer' }}>×</button>
              </div>
              <div style={{ padding: '24px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', color: '#ffffff', fontSize: '14px' }}>User</label>
                    <div style={{ padding: '10px', backgroundColor: '#111827', border: '1px solid #374151', borderRadius: '8px', color: '#ffffff' }}>
                      {users.find(u => u.id.toString() === createForm.user_id)?.full_name || users.find(u => u.id.toString() === createForm.user_id)?.username || 'Unknown'}
                    </div>
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', color: '#ffffff', fontSize: '14px' }}>Company Entity</label>
                    <div style={{ padding: '10px', backgroundColor: '#111827', border: '1px solid #374151', borderRadius: '8px', color: '#ffffff' }}>
                      {getEntityLabel(createForm.company_entity)}
                    </div>
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', color: '#ffffff', fontSize: '14px' }}>Currency</label>
                    <select
                      value={createForm.currency}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, currency: e.target.value as CurrencyCode }))}
                      style={{ width: '100%', padding: '10px', backgroundColor: '#111827', border: '1px solid #374151', borderRadius: '8px', color: '#FFFFFF' }}
                    >
                      {SUPPORTED_CURRENCIES.map(curr => (
                        <option key={curr.code} value={curr.code}>
                          {curr.symbol} {curr.code} - {curr.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', color: '#ffffff', fontSize: '14px' }}>Invoice Date</label>
                    <input
                      type="date"
                      value={createForm.invoice_date}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, invoice_date: e.target.value }))}
                      style={{ width: '100%', padding: '10px', backgroundColor: '#111827', border: '1px solid #374151', borderRadius: '8px', color: '#FFFFFF' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', color: '#ffffff', fontSize: '14px' }}>Due Date</label>
                    <input
                      type="date"
                      value={createForm.due_date}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, due_date: e.target.value }))}
                      style={{ width: '100%', padding: '10px', backgroundColor: '#111827', border: '1px solid #374151', borderRadius: '8px', color: '#FFFFFF' }}
                    />
                  </div>
                </div>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', color: '#ffffff', fontSize: '14px' }}>Description</label>
                  <textarea
                    value={createForm.description}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, description: e.target.value }))}
                    rows={2}
                    style={{ width: '100%', padding: '10px', backgroundColor: '#111827', border: '1px solid #374151', borderRadius: '8px', color: '#FFFFFF', resize: 'vertical' }}
                  />
                </div>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', color: '#ffffff', fontSize: '14px' }}>Notes (Internal)</label>
                  <textarea
                    value={createForm.notes}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Internal notes (not shown on invoice)..."
                    rows={2}
                    style={{ width: '100%', padding: '10px', backgroundColor: '#111827', border: '1px solid #374151', borderRadius: '8px', color: '#FFFFFF', resize: 'vertical' }}
                  />
                </div>

                {/* Line Items */}
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <label style={{ color: '#ffffff', fontSize: '16px', fontWeight: '600' }}>Line Items</label>
                    <button onClick={addLineItem} style={{ padding: '6px 12px', backgroundColor: '#10B981', color: '#FFFFFF', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>
                      + Add Item
                    </button>
                  </div>
                  {createForm.line_items.map((item, index) => (
                    <div key={index} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr auto', gap: '8px', marginBottom: '8px', alignItems: 'end' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: '4px', color: '#ffffff', fontSize: '14px' }}>Description</label>
                        <input
                          type="text"
                          value={item.description}
                          onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                          style={{ width: '100%', padding: '8px', backgroundColor: '#111827', border: '1px solid #374151', borderRadius: '6px', color: '#FFFFFF', fontSize: '13px' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '4px', color: '#ffffff', fontSize: '14px' }}>Category</label>
                        <input
                          type="text"
                          value={item.expense_category}
                          onChange={(e) => updateLineItem(index, 'expense_category', e.target.value)}
                          placeholder="e.g. Travel"
                          style={{ width: '100%', padding: '8px', backgroundColor: '#111827', border: '1px solid #374151', borderRadius: '6px', color: '#FFFFFF', fontSize: '13px' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '4px', color: '#ffffff', fontSize: '14px' }}>Qty</label>
                        <input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) => updateLineItem(index, 'quantity', parseInt(e.target.value) || 1)}
                          style={{ width: '100%', padding: '8px', backgroundColor: '#111827', border: '1px solid #374151', borderRadius: '6px', color: '#FFFFFF', fontSize: '13px' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '4px', color: '#ffffff', fontSize: '14px' }}>Unit Price</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={item.unit_price}
                          onChange={(e) => updateLineItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                          style={{ width: '100%', padding: '8px', backgroundColor: '#111827', border: '1px solid #374151', borderRadius: '6px', color: '#FFFFFF', fontSize: '13px' }}
                        />
                      </div>
                      <button
                        onClick={() => removeLineItem(index)}
                        disabled={createForm.line_items.length === 1}
                        style={{ padding: '8px 12px', backgroundColor: '#7F1D1D', color: '#FCA5A5', border: 'none', borderRadius: '6px', cursor: 'pointer', opacity: createForm.line_items.length === 1 ? 0.4 : 1 }}
                      >
                        🗑️
                      </button>
                    </div>
                  ))}
                </div>

                {/* Totals */}
                <div style={{ backgroundColor: '#111827', borderRadius: '8px', padding: '16px', marginBottom: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={{ color: '#ffffff' }}>Subtotal:</span>
                    <span style={{ color: '#ffffff' }}>{formatCurrency(calculateTotal().subtotal)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ color: '#ffffff' }}>Tax:</span>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        value={createForm.tax_rate}
                        onChange={(e) => setCreateForm(prev => ({ ...prev, tax_rate: parseFloat(e.target.value) || 0 }))}
                        style={{ width: '60px', padding: '4px 8px', backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '4px', color: '#FFFFFF', fontSize: '13px', textAlign: 'right' }}
                      />
                      <span style={{ color: '#ffffff' }}>%</span>
                    </div>
                    <span style={{ color: '#ffffff' }}>{formatCurrency(calculateTotal().taxAmount)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #374151', paddingTop: '8px' }}>
                    <span style={{ color: '#ffffff', fontWeight: '600' }}>Total:</span>
                    <span style={{ color: '#10B981', fontWeight: '700', fontSize: '18px' }}>{formatCurrency(calculateTotal().total)}</span>
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '16px', borderTop: '1px solid #374151' }}>
                  <button
                    onClick={() => { setShowEditModal(false); resetCreateForm(); }}
                    style={{ padding: '10px 20px', backgroundColor: '#374151', color: '#ffffff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleEditInvoice}
                    disabled={loading}
                    style={{ padding: '10px 20px', backgroundColor: '#3B82F6', color: '#FFFFFF', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', opacity: loading ? 0.6 : 1 }}
                  >
                    {loading ? 'Saving...' : '💾 Save Changes'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Invoice Details Modal */}
        {showDetailsModal && selectedInvoice && (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '12px' }}>
            <div className="modal-content" style={{ backgroundColor: '#1F2937', borderRadius: '16px', width: '100%', maxWidth: '900px', maxHeight: '90vh', overflow: 'auto', border: '1px solid #374151' }}>
              <div style={{ padding: '20px', borderBottom: '1px solid #374151', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#F59E0B' }}>📄 {selectedInvoice.invoice_number}</h2>
                  <p style={{ color: '#ffffff', fontSize: '14px', marginTop: '4px' }}>
                    Created: {formatDate(selectedInvoice.created_at)}
                  </p>
                </div>
                <button onClick={() => { setShowDetailsModal(false); setSelectedInvoice(null); }} style={{ background: 'none', border: 'none', color: '#ffffff', fontSize: '24px', cursor: 'pointer' }}>×</button>
              </div>

              <div style={{ padding: '24px' }}>
                {/* Invoice Info Grid */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px', marginBottom: '24px' }}>
                  <div style={{ backgroundColor: '#111827', padding: '16px', borderRadius: '8px' }}>
                    <div style={{ color: '#ffffff', fontSize: '14px', marginBottom: '4px' }}>User</div>
                    <div style={{ color: '#ffffff', fontWeight: '500' }}>{selectedInvoice.user?.full_name || selectedInvoice.user?.username}</div>
                    <div style={{ color: '#ffffff', fontSize: '13px' }}>{selectedInvoice.user?.email}</div>
                  </div>
                  <div style={{ backgroundColor: '#111827', padding: '16px', borderRadius: '8px' }}>
                    <div style={{ color: '#ffffff', fontSize: '14px', marginBottom: '4px' }}>Company Entity</div>
                    <div style={{ color: getEntityAccentColor(selectedInvoice.company_entity), fontWeight: '500' }}>
                      {getEntityFullLabel(selectedInvoice.company_entity)}
                    </div>
                  </div>
                  <div style={{ backgroundColor: '#111827', padding: '16px', borderRadius: '8px' }}>
                    <div style={{ color: '#ffffff', fontSize: '14px', marginBottom: '4px' }}>Status</div>
                    <span style={{
                      padding: '4px 10px',
                      borderRadius: '999px',
                      backgroundColor: getStatusBadgeColor(selectedInvoice.status).bg,
                      color: getStatusBadgeColor(selectedInvoice.status).text,
                      fontSize: '13px',
                      fontWeight: '500'
                    }}>
                      {selectedInvoice.status.replace('_', ' ')}
                    </span>
                  </div>
                  <div style={{ backgroundColor: '#111827', padding: '16px', borderRadius: '8px' }}>
                    <div style={{ color: '#ffffff', fontSize: '14px', marginBottom: '4px' }}>Invoice Date</div>
                    <div style={{ color: '#ffffff', fontWeight: '500' }}>{formatDate(selectedInvoice.invoice_date)}</div>
                  </div>
                  <div style={{ backgroundColor: '#111827', padding: '16px', borderRadius: '8px' }}>
                    <div style={{ color: '#ffffff', fontSize: '14px', marginBottom: '4px' }}>Due Date</div>
                    <div style={{ color: '#ffffff', fontWeight: '500' }}>{selectedInvoice.due_date ? formatDate(selectedInvoice.due_date) : 'Not set'}</div>
                  </div>
                  <div style={{ backgroundColor: '#111827', padding: '16px', borderRadius: '8px' }}>
                    <div style={{ color: '#ffffff', fontSize: '14px', marginBottom: '4px' }}>Total Amount</div>
                    <div style={{ color: '#10B981', fontWeight: '700', fontSize: '18px' }}>{formatCurrency(selectedInvoice.total_amount)}</div>
                  </div>
                </div>

                {/* Payment Requested Alert */}
                {selectedInvoice.status === 'approved' && selectedInvoice.payment_requested && (
                  <div style={{
                    backgroundColor: '#7F1D1D',
                    border: '2px solid #DC2626',
                    borderRadius: '8px',
                    padding: '16px',
                    marginBottom: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    <span style={{ fontSize: '24px' }}>💳</span>
                    <div>
                      <div style={{ color: '#FCA5A5', fontWeight: '600', fontSize: '16px' }}>Payment Requested</div>
                      <div style={{ color: '#F87171', fontSize: '13px' }}>
                        User requested payment on {selectedInvoice.payment_requested_at ? formatDate(selectedInvoice.payment_requested_at) : 'Unknown date'}
                      </div>
                    </div>
                    <button
                      onClick={() => openProcessPaymentModal(selectedInvoice)}
                      style={{
                        marginLeft: 'auto',
                        padding: '10px 20px',
                        backgroundColor: '#DC2626',
                        color: '#FFFFFF',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: '600'
                      }}
                    >
                      Process Payment
                    </button>
                  </div>
                )}

                {/* Description */}
                {selectedInvoice.description && (
                  <div style={{ marginBottom: '24px' }}>
                    <div style={{ color: '#ffffff', fontSize: '14px', marginBottom: '8px' }}>Description</div>
                    <div style={{ backgroundColor: '#111827', padding: '12px', borderRadius: '8px', color: '#ffffff' }}>
                      {selectedInvoice.description}
                    </div>
                  </div>
                )}

                {/* Line Items */}
                <div style={{ marginBottom: '24px' }}>
                  <div style={{ color: '#ffffff', fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>Line Items</div>
                  <div style={{ backgroundColor: '#111827', borderRadius: '8px', overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid #374151' }}>
                          <th style={{ padding: '12px', textAlign: 'left', color: '#ffffff', fontSize: '14px' }}>Description</th>
                          <th style={{ padding: '12px', textAlign: 'left', color: '#ffffff', fontSize: '14px' }}>Category</th>
                          <th style={{ padding: '12px', textAlign: 'right', color: '#ffffff', fontSize: '14px' }}>Qty</th>
                          <th style={{ padding: '12px', textAlign: 'right', color: '#ffffff', fontSize: '14px' }}>Unit Price</th>
                          <th style={{ padding: '12px', textAlign: 'right', color: '#ffffff', fontSize: '14px' }}>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedInvoice.line_items && selectedInvoice.line_items.length > 0 ? (
                          selectedInvoice.line_items.map((item, idx) => (
                            <tr key={idx} style={{ borderBottom: '1px solid #1F2937' }}>
                              <td style={{ padding: '12px', color: '#ffffff' }}>{item.description}</td>
                              <td style={{ padding: '12px', color: '#ffffff' }}>{item.expense_category || '-'}</td>
                              <td style={{ padding: '12px', textAlign: 'right', color: '#ffffff' }}>{item.quantity}</td>
                              <td style={{ padding: '12px', textAlign: 'right', color: '#ffffff' }}>{formatCurrency(item.unit_price)}</td>
                              <td style={{ padding: '12px', textAlign: 'right', color: '#10B981', fontWeight: '500' }}>{formatCurrency(item.amount)}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={5} style={{ padding: '24px', textAlign: 'center', color: '#ffffff' }}>
                              Loading line items...
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Totals */}
                <div style={{ backgroundColor: '#111827', borderRadius: '8px', padding: '16px', marginBottom: '24px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={{ color: '#ffffff' }}>Subtotal:</span>
                    <span style={{ color: '#ffffff' }}>{formatCurrency(selectedInvoice.subtotal)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={{ color: '#ffffff' }}>Tax ({selectedInvoice.tax_rate}%):</span>
                    <span style={{ color: '#ffffff' }}>{formatCurrency(selectedInvoice.tax_amount)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #374151', paddingTop: '8px' }}>
                    <span style={{ color: '#ffffff', fontWeight: '600' }}>Total:</span>
                    <span style={{ color: '#10B981', fontWeight: '700', fontSize: '18px' }}>{formatCurrency(selectedInvoice.total_amount)}</span>
                  </div>
                </div>

                {/* Attachments Section */}
                <div style={{ marginBottom: '24px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <div style={{ color: '#ffffff', fontSize: '16px', fontWeight: '600' }}>📎 Attachments</div>
                    <label style={{
                      padding: '8px 14px',
                      backgroundColor: '#374151',
                      color: '#FFFFFF',
                      borderRadius: '6px',
                      cursor: uploadingAttachment ? 'not-allowed' : 'pointer',
                      fontSize: '13px',
                      opacity: uploadingAttachment ? 0.5 : 1
                    }}>
                      {uploadingAttachment ? '⏳ Uploading...' : '➕ Upload File'}
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx"
                        onChange={(e) => handleFileUpload(e, selectedInvoice.id)}
                        style={{ display: 'none' }}
                        disabled={uploadingAttachment}
                      />
                    </label>
                  </div>
                  <div style={{ backgroundColor: '#111827', borderRadius: '8px', padding: '12px' }}>
                    {selectedInvoice.attachments && selectedInvoice.attachments.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {selectedInvoice.attachments.map((att) => (
                          <div key={att.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px', backgroundColor: '#1F2937', borderRadius: '6px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <span style={{ fontSize: '20px' }}>
                                {att.file_type?.includes('pdf') ? '📄' :
                                 att.file_type?.includes('image') ? '🖼️' : '📎'}
                              </span>
                              <div>
                                <div style={{ color: '#ffffff', fontSize: '14px' }}>{att.file_name}</div>
                                <div style={{ color: '#ffffff', fontSize: '14px' }}>{att.attachment_type} • {formatDate(att.created_at)}</div>
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              {att.file_url && !att.file_url.startsWith('local:') && (
                                <a
                                  href={att.file_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ padding: '6px 10px', backgroundColor: '#3B82F6', color: '#FFFFFF', borderRadius: '4px', fontSize: '14px', textDecoration: 'none' }}
                                >
                                  View
                                </a>
                              )}
                              <button
                                onClick={() => handleDeleteAttachment(att.id, selectedInvoice.id)}
                                style={{ padding: '6px 10px', backgroundColor: '#7F1D1D', color: '#FCA5A5', border: 'none', borderRadius: '4px', fontSize: '14px', cursor: 'pointer' }}
                              >
                                🗑️
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ textAlign: 'center', color: '#ffffff', padding: '16px' }}>
                        No attachments yet. Click "Upload File" to add receipts or documents.
                      </div>
                    )}
                  </div>
                </div>

                {/* Status Actions */}
                <div style={{ borderTop: '1px solid #374151', paddingTop: '20px' }}>
                  <div style={{ color: '#ffffff', fontSize: '14px', fontWeight: '600', marginBottom: '12px' }}>Actions</div>
                  <div className="modal-actions" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    {/* Edit button - only for draft/pending invoices */}
                    {['draft', 'pending_approval'].includes(selectedInvoice.status) && (
                      <button
                        onClick={() => {
                          openEditModal(selectedInvoice);
                          setShowDetailsModal(false);
                        }}
                        disabled={loading}
                        style={{ padding: '10px 16px', backgroundColor: '#3B82F6', color: '#FFFFFF', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}
                      >
                        ✏️ Edit Invoice
                      </button>
                    )}
                    {/* Duplicate button - always available */}
                    <button
                      onClick={() => {
                        handleDuplicateInvoice(selectedInvoice);
                        setShowDetailsModal(false);
                      }}
                      disabled={loading}
                      style={{ padding: '10px 16px', backgroundColor: '#8B5CF6', color: '#FFFFFF', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}
                    >
                      📋 Duplicate
                    </button>
                    {/* Email to User button - always available */}
                    <button
                      onClick={() => handleEmailInvoiceToUser(selectedInvoice)}
                      disabled={loading}
                      style={{ padding: '10px 16px', backgroundColor: '#0EA5E9', color: '#FFFFFF', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}
                    >
                      📧 Email to User
                    </button>
                    {selectedInvoice.status === 'draft' && (
                      <button
                        onClick={() => handleUpdateStatus(selectedInvoice.id, 'pending_approval')}
                        disabled={loading}
                        style={{ padding: '10px 16px', backgroundColor: '#F59E0B', color: '#000000', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}
                      >
                        📤 Submit for Approval
                      </button>
                    )}
                    {selectedInvoice.status === 'pending_approval' && (
                      <>
                        <button
                          onClick={() => handleUpdateStatus(selectedInvoice.id, 'approved')}
                          disabled={loading}
                          style={{ padding: '10px 16px', backgroundColor: '#10B981', color: '#FFFFFF', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}
                        >
                          ✅ Approve
                        </button>
                        <button
                          onClick={() => {
                            const reason = prompt('Enter rejection reason:');
                            if (reason) handleUpdateStatus(selectedInvoice.id, 'rejected', reason);
                          }}
                          disabled={loading}
                          style={{ padding: '10px 16px', backgroundColor: '#DC2626', color: '#FFFFFF', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}
                        >
                          ❌ Reject
                        </button>
                      </>
                    )}
                    {selectedInvoice.status === 'approved' && (
                      <>
                        {selectedInvoice.payment_requested && (
                          <button
                            onClick={() => openProcessPaymentModal(selectedInvoice)}
                            disabled={loading}
                            style={{ padding: '10px 16px', backgroundColor: '#DC2626', color: '#FFFFFF', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}
                          >
                            💳 Process Payment
                          </button>
                        )}
                        <button
                          onClick={() => handleUpdateStatus(selectedInvoice.id, 'paid')}
                          disabled={loading}
                          style={{ padding: '10px 16px', backgroundColor: '#3B82F6', color: '#FFFFFF', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}
                        >
                          💰 Mark as Paid (Quick)
                        </button>
                      </>
                    )}
                    {['draft', 'pending_approval'].includes(selectedInvoice.status) && (
                      <button
                        onClick={() => {
                          if (confirm('Are you sure you want to void this invoice?')) {
                            handleUpdateStatus(selectedInvoice.id, 'voided');
                          }
                        }}
                        disabled={loading}
                        style={{ padding: '10px 16px', backgroundColor: '#374151', color: '#ffffff', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
                      >
                        🚫 Void Invoice
                      </button>
                    )}
                    <button
                      onClick={async () => {
                        if (!selectedInvoice) return;
                        try {
                          const invoiceData: InvoiceData = {
                            invoice_number: selectedInvoice.invoice_number,
                            invoice_date: selectedInvoice.invoice_date,
                            due_date: selectedInvoice.due_date,
                            company_entity: selectedInvoice.company_entity as any,
                            status: selectedInvoice.status,
                            subtotal: selectedInvoice.subtotal,
                            tax_rate: selectedInvoice.tax_rate,
                            tax_amount: selectedInvoice.tax_amount,
                            total_amount: selectedInvoice.total_amount,
                            currency: selectedInvoice.currency || 'USD',
                            description: selectedInvoice.description,
                            notes: selectedInvoice.notes,
                            user: {
                              full_name: selectedInvoice.user?.full_name || null,
                              username: selectedInvoice.user?.username || 'Unknown',
                              email: selectedInvoice.user?.email || ''
                            },
                            line_items: (selectedInvoice.line_items || []).map(li => ({
                              description: li.description,
                              expense_category: li.expense_category,
                              quantity: li.quantity,
                              unit_price: li.unit_price,
                              amount: li.amount,
                              expense_date: li.expense_date
                            }))
                          };
                          await downloadInvoicePdf(invoiceData);
                          setSuccess('PDF downloaded successfully');
                        } catch (err) {
                          console.error('Error downloading PDF:', err);
                          setError('Failed to download PDF');
                        }
                      }}
                      style={{ padding: '10px 16px', backgroundColor: '#2563EB', color: '#FFFFFF', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
                    >
                      📥 Download PDF
                    </button>
                    <button
                      onClick={() => { setShowDetailsModal(false); setSelectedInvoice(null); }}
                      style={{ padding: '10px 16px', backgroundColor: '#374151', color: '#ffffff', border: 'none', borderRadius: '8px', cursor: 'pointer', marginLeft: 'auto' }}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Process Payment Modal */}
        {showProcessPaymentModal && selectedInvoice && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1001,
            padding: '12px'
          }}>
            <div className="modal-content" style={{
              backgroundColor: '#1F2937',
              borderRadius: '16px',
              width: '100%',
              maxWidth: '600px',
              maxHeight: '90vh',
              overflow: 'auto'
            }}>
              <div style={{ padding: '20px' }}>
                {/* Header */}
                <div style={{ marginBottom: '20px' }}>
                  <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#ffffff', margin: 0 }}>
                    💳 Process Payment
                  </h2>
                  <p style={{ color: '#ffffff', marginTop: '8px', fontSize: '14px' }}>
                    Invoice: {selectedInvoice.invoice_number} | Amount: {formatCurrency(selectedInvoice.total_amount)}
                  </p>
                </div>

                {/* Invoice Summary */}
                <div style={{ backgroundColor: '#111827', borderRadius: '8px', padding: '14px', marginBottom: '20px' }}>
                  <div className="modal-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '12px' }}>
                    <div>
                      <div style={{ color: '#ffffff', fontSize: '14px' }}>Recipient</div>
                      <div style={{ color: '#ffffff', fontWeight: '500' }}>
                        {selectedInvoice.user?.full_name || selectedInvoice.user?.username}
                      </div>
                    </div>
                    <div>
                      <div style={{ color: '#ffffff', fontSize: '14px' }}>Email</div>
                      <div style={{ color: '#ffffff', fontWeight: '500' }}>{selectedInvoice.user?.email}</div>
                    </div>
                    <div>
                      <div style={{ color: '#ffffff', fontSize: '14px' }}>Entity</div>
                      <div style={{ color: getEntityAccentColor(selectedInvoice.company_entity), fontWeight: '500' }}>
                        {getEntityLabel(selectedInvoice.company_entity)}
                      </div>
                    </div>
                    <div>
                      <div style={{ color: '#ffffff', fontSize: '14px' }}>Amount</div>
                      <div style={{ color: '#10B981', fontWeight: '700', fontSize: '18px' }}>
                        {formatCurrency(selectedInvoice.total_amount)}
                      </div>
                    </div>
                  </div>
                </div>

                {/* User's Payment Methods */}
                {userPaymentMethods.length > 0 && (
                  <div style={{ backgroundColor: '#1E3A5F', borderRadius: '8px', padding: '16px', marginBottom: '24px', border: '1px solid #3B82F6' }}>
                    <h4 style={{ color: '#60A5FA', fontSize: '14px', fontWeight: '600', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span>💳</span> User's Saved Payment Methods
                    </h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {userPaymentMethods.map((method) => (
                        <div
                          key={method.id}
                          style={{
                            backgroundColor: '#111827',
                            borderRadius: '6px',
                            padding: '12px',
                            border: method.is_default ? '2px solid #10B981' : '1px solid #374151'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                            <span style={{ color: '#ffffff', fontWeight: '500', fontSize: '14px' }}>
                              {method.method_type === 'bank_transfer' ? '🏦' : '💰'} {method.method_name}
                            </span>
                            {method.is_default && (
                              <span style={{ backgroundColor: '#10B981', color: '#FFFFFF', fontSize: '13px', padding: '2px 8px', borderRadius: '4px', fontWeight: '600' }}>
                                DEFAULT
                              </span>
                            )}
                          </div>
                          {method.method_type === 'bank_transfer' && method.bank_name && (
                            <div style={{ color: '#ffffff', fontSize: '14px' }}>
                              Bank: <span style={{ color: '#ffffff' }}>{method.bank_name}</span>
                            </div>
                          )}
                          {method.method_type === 'crypto' && (
                            <>
                              {method.network_name && (
                                <div style={{ color: '#ffffff', fontSize: '14px' }}>
                                  Network: <span style={{ color: '#ffffff' }}>{method.network_name}</span>
                                </div>
                              )}
                              {method.wallet_address && (
                                <div style={{ color: '#ffffff', fontSize: '14px', marginTop: '4px' }}>
                                  Wallet: <span style={{ color: '#FCD34D', fontFamily: 'monospace', fontSize: '13px', wordBreak: 'break-all' }}>{method.wallet_address}</span>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                    <p style={{ color: '#ffffff', fontSize: '13px', marginTop: '10px' }}>
                      💡 Use the user's preferred payment method above when processing this payment.
                    </p>
                  </div>
                )}

                {/* No Payment Methods Message */}
                {userPaymentMethods.length === 0 && (
                  <div style={{ backgroundColor: '#374151', borderRadius: '8px', padding: '12px', marginBottom: '24px' }}>
                    <p style={{ color: '#ffffff', fontSize: '14px', margin: 0 }}>
                      ⚠️ This user has no saved payment methods. Consider asking them to add their banking details to their profile.
                    </p>
                  </div>
                )}

                {/* Payment Form */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' }}>
                  {/* Payment Method */}
                  <div>
                    <label style={{ display: 'block', color: '#ffffff', marginBottom: '6px', fontSize: '14px' }}>
                      Payment Method *
                    </label>
                    <select
                      value={processPaymentForm.payment_method}
                      onChange={(e) => setProcessPaymentForm(prev => ({ ...prev, payment_method: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        backgroundColor: '#111827',
                        border: '1px solid #374151',
                        borderRadius: '8px',
                        color: '#ffffff',
                        fontSize: '14px'
                      }}
                    >
                      <option value="bank_transfer">🏦 Bank Transfer</option>
                      <option value="crypto_usdt">💰 Crypto (USDT)</option>
                      <option value="crypto_other">🪙 Crypto (Other)</option>
                      <option value="cash">💵 Cash</option>
                      <option value="paypal">💳 PayPal</option>
                      <option value="other">📋 Other</option>
                    </select>
                  </div>

                  {/* Payment Reference */}
                  <div>
                    <label style={{ display: 'block', color: '#ffffff', marginBottom: '6px', fontSize: '14px' }}>
                      Payment Reference / Receipt Number
                    </label>
                    <input
                      type="text"
                      value={processPaymentForm.payment_reference}
                      onChange={(e) => setProcessPaymentForm(prev => ({ ...prev, payment_reference: e.target.value }))}
                      placeholder="e.g., Bank reference, receipt number"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        backgroundColor: '#111827',
                        border: '1px solid #374151',
                        borderRadius: '8px',
                        color: '#ffffff',
                        fontSize: '14px'
                      }}
                    />
                  </div>

                  {/* Transaction Hash (for crypto) */}
                  {processPaymentForm.payment_method.includes('crypto') && (
                    <div>
                      <label style={{ display: 'block', color: '#ffffff', marginBottom: '6px', fontSize: '14px' }}>
                        Transaction Hash
                      </label>
                      <input
                        type="text"
                        value={processPaymentForm.transaction_hash}
                        onChange={(e) => setProcessPaymentForm(prev => ({ ...prev, transaction_hash: e.target.value }))}
                        placeholder="Enter blockchain transaction hash"
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          backgroundColor: '#111827',
                          border: '1px solid #374151',
                          borderRadius: '8px',
                          color: '#ffffff',
                          fontSize: '14px',
                          fontFamily: 'monospace'
                        }}
                      />
                    </div>
                  )}

                  {/* Proof of Payment URL */}
                  <div>
                    <label style={{ display: 'block', color: '#ffffff', marginBottom: '6px', fontSize: '14px' }}>
                      Proof of Payment URL (Screenshot/Document)
                    </label>
                    <input
                      type="url"
                      value={processPaymentForm.proof_of_payment_url}
                      onChange={(e) => setProcessPaymentForm(prev => ({ ...prev, proof_of_payment_url: e.target.value }))}
                      placeholder="https://..."
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        backgroundColor: '#111827',
                        border: '1px solid #374151',
                        borderRadius: '8px',
                        color: '#ffffff',
                        fontSize: '14px'
                      }}
                    />
                  </div>

                  {/* Admin Notes */}
                  <div>
                    <label style={{ display: 'block', color: '#ffffff', marginBottom: '6px', fontSize: '14px' }}>
                      Admin Notes (Optional)
                    </label>
                    <textarea
                      value={processPaymentForm.admin_notes}
                      onChange={(e) => setProcessPaymentForm(prev => ({ ...prev, admin_notes: e.target.value }))}
                      placeholder="Any additional notes about this payment..."
                      rows={3}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        backgroundColor: '#111827',
                        border: '1px solid #374151',
                        borderRadius: '8px',
                        color: '#ffffff',
                        fontSize: '14px',
                        resize: 'vertical'
                      }}
                    />
                  </div>
                </div>

                {/* Previous Payments (if any) */}
                {invoicePayments.length > 0 && (
                  <div style={{ marginBottom: '24px' }}>
                    <div style={{ color: '#ffffff', fontSize: '14px', fontWeight: '600', marginBottom: '12px' }}>
                      Previous Payments
                    </div>
                    <div style={{ backgroundColor: '#111827', borderRadius: '8px', overflow: 'hidden' }}>
                      {invoicePayments.map((payment) => (
                        <div key={payment.id} style={{ padding: '12px', borderBottom: '1px solid #374151' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: '#ffffff' }}>{formatDate(payment.payment_date)}</span>
                            <span style={{ color: '#10B981', fontWeight: '600' }}>{formatCurrency(payment.payment_amount)}</span>
                          </div>
                          <div style={{ color: '#ffffff', fontSize: '13px', marginTop: '4px' }}>
                            {payment.payment_method} {payment.payment_reference && `• ${payment.payment_reference}`}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => {
                      setShowProcessPaymentModal(false);
                      setProcessPaymentForm({
                        payment_method: 'bank_transfer',
                        payment_reference: '',
                        transaction_hash: '',
                        proof_of_payment_url: '',
                        admin_notes: ''
                      });
                    }}
                    style={{
                      padding: '12px 24px',
                      backgroundColor: '#374151',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: '500'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleProcessPayment}
                    disabled={loading || !processPaymentForm.payment_method}
                    style={{
                      padding: '12px 24px',
                      backgroundColor: loading ? '#374151' : '#10B981',
                      color: '#FFFFFF',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      fontWeight: '600'
                    }}
                  >
                    {loading ? '⏳ Processing...' : '✅ Confirm Payment'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InvoiceManager;